import React, { useEffect, useMemo, useRef, useState } from "react";
import { mcSyncNeoCatalogue } from '../utils/mcCloudSync';
import McCountProductPicker from "../components/McCountProductPicker";
import { countProductSelectionKey, uniqueCountProducts, selectedCountProducts } from "../utils/mcCountProductScope";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { flushSync } from "react-dom";
import { idbGetJson, idbSetJson } from "../utils/mcStorageEngine";
import { useNavigate } from "react-router-dom";
import { McPageHeader, McPageFooter } from "../components/McPageLayout";
import "./mcImportEngine.css";
import {
  archiveSessionRecord,
  buildEmptyState,
  buildDuplicateMergeSummary,
  buildPreparedData,
  buildSessionPayloadFromEngine,
  loadArchivedSessionRecords,
  loadLatestImportEngineState,
  loadSessionRecords,
  savePreparedRowsToSessionSeed,
  saveEngineState,
  saveEngineStateDurable,
  saveCategoryBackupDurable,
  saveCategoryConfigurationDurable,
  saveDuplicateReviewDecisions,
  hydrateDuplicateReviewDecisions,
  hydrateCategoryConfigurationFromCloud,
  loadEngineState,
  saveSessionDetailFromPayloadDurable,
  upsertSessionRecordDurable,
  MC_ENGINE_EVENT,
  recomputeEngineState,
  detectUniversalImportProfile,
} from "../utils/mcEngineData";
import {
  buildProfileDisplayName,
  formatStoreLabel,
  normalizeStoreValue,
  getActiveStoreScope,
  getProfileAllowedSectionIds,
  profileHasAllSectionAccess,
  isAdminProfile,
  useAdminStoreView,
  useCurrentMcProfile,
} from "../utils/profileAccess";
import { MC_SYNC_EVENT, MC_SYNC_READY_EVENT, mcSetCloudItemImmediate } from "../utils/mcCloudSync";
import { getMcActiveOrgId } from "../utils/mcCloudSync";
import { recordAcceptedMcCountStart } from "../../billing/billingPreviewService";
import {
  activeSections,
  activeCycles,
  formatSectionLabel,
  formatCycleLabel,
  normalizeSectionId,
  normalizeCycleId,
  saveSections,
  MC_SECTION_EVENT,
  MC_CYCLE_EVENT,
  MC_SECTIONS_KEY,
} from "../utils/mcSections";
import { MC_SITE_EVENT, detectSiteHeaders, loadSites, resolveSite } from "../utils/mcOrgData";
import {
  loadActiveInventorySnapshot,
  peekActiveInventorySnapshot,
  subscribeActiveInventorySnapshot,
  MC_ACTIVE_INVENTORY_EVENT,
} from "../utils/mcActiveInventory";
import { loadStorageMovementLedger } from "../utils/mcStorageMovementLedger";


function formatNumber(value) {
  return Number(value || 0).toFixed(2);
}

function formatMoney(value) {
  return `R${Number(value || 0).toFixed(2)}`;
}

function formatRevisionTimestamp(value) {
  const parsed = Date.parse(value || "");
  if (!Number.isFinite(parsed)) return "Timestamp unavailable";
  return new Intl.DateTimeFormat("en-ZA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(parsed));
}

function isMorpheusLightTheme() {
  if (typeof document === "undefined") return false;
  const html = document.documentElement;
  const body = document.body;
  const classText = `${html?.className || ""} ${body?.className || ""}`.toLowerCase();
  const dataTheme = `${html?.dataset?.theme || ""} ${body?.dataset?.theme || ""} ${html?.dataset?.mcTheme || ""} ${body?.dataset?.mcTheme || ""}`.toLowerCase();
  const themeText = `${classText} ${dataTheme}`;
  if (/mc-theme-light|mc-light-theme|mc-light|\bis-light\b/.test(themeText)) return true;
  if (/mc-theme-dark|mc-dark-theme|mc-dark|\bis-dark\b/.test(themeText)) return false;
  if (/light/.test(themeText)) return true;
  if (/dark/.test(themeText)) return false;
  return false;
}

function safePdfFilePart(value, fallback = "All-Sites") {
  const text = String(value || "").trim() || fallback;
  return text.replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, "-").replace(/-+/g, "-");
}

function clean(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function resolveInventoryHeaderForScope(scope, headers = [], engine = {}) {
  if (
    String(engine?.config?.dataSourceMode || "csv").toLowerCase() === "pos" ||
    (headers || []).includes("system_soh")
  ) {
    return "system_soh";
  }

  // Lightspeed / column-based exports can contain many inventory_<site> columns.
  // These must win over any falsely detected row-site field (for example an
  // outlet_tax_<site> column). Resolve the selected operational site directly
  // to its inventory column before consulting the universal profile.
  const inventoryHeaders = (headers || []).filter((header) =>
    /^inventory_/i.test(String(header || "").trim()),
  );
  const normalizedScope = normalizeStoreValue(scope || "ALL");
  if (inventoryHeaders.length) {
    if (normalizedScope && normalizedScope !== "ALL") {
      const compare = formatStoreLabel(normalizedScope).toLowerCase();
      const matchedInventoryHeader = inventoryHeaders.find(
        (header) => formatStoreLabel(header).toLowerCase() === compare,
      );
      if (matchedInventoryHeader) return matchedInventoryHeader;
    }
    if (inventoryHeaders.includes(engine?.mapping?.systemSoh)) {
      return engine.mapping.systemSoh;
    }
    return inventoryHeaders[0] || "";
  }

  // Row-based CSVs use one shared SOH column and a separate Site column.
  // A site name such as "Bellville" must never replace the mapped SOH header.
  const universalProfile = detectUniversalImportProfile(
    headers || [],
    engine?.rawRows || [],
    engine?.config?.storeName || "",
  );
  if (universalProfile?.layout === "row_site") {
    return (
      universalProfile?.fields?.systemSoh?.header ||
      engine?.mapping?.systemSoh ||
      ""
    );
  }

  // Also protect generic non-Lightspeed stock headers even when the source
  // profile cannot be reconstructed from a compacted snapshot.
  const genericSohHeader = (headers || []).find((header) =>
    /^(soh|stock on hand|stock|quantity on hand|qty on hand|quantity|qty)$/i.test(
      String(header || "").trim(),
    ),
  );
  if (genericSohHeader) return genericSohHeader;

  if (!normalizedScope || normalizedScope === "ALL") {
    return (
      engine?.mapping?.systemSoh ||
      headers.find((header) => /^inventory_/i.test(String(header || ""))) ||
      ""
    );
  }
  const compare = formatStoreLabel(normalizedScope).toLowerCase();
  const matchedHeader = (headers || []).find(
    (header) => formatStoreLabel(header).toLowerCase() === compare,
  );
  return matchedHeader || engine?.mapping?.systemSoh || "";
}

function sameStore(record, engine) {
  // The operational site is the selected MatrixOS site, never the CSV SOH
  // header. Row-based files commonly use a shared header such as "SOH" for
  // every site; comparing against that header made every new site look like it
  // already owned the same active session.
  const targetStore = engine?.config?.storeName || engine?.storeName || "";
  const recordStore =
    record?.siteName ||
    record?.storeName ||
    record?.store ||
    record?.siteId ||
    record?.siteKey ||
    "";

  const targetToken = formatStoreLabel(targetStore).trim().toLowerCase();
  const recordToken = formatStoreLabel(recordStore).trim().toLowerCase();

  // Generic inventory headers are not site identities and cannot participate
  // in active-slot conflict detection.
  if (!targetToken || !recordToken || ["soh", "stock", "system soh"].includes(recordToken)) {
    return false;
  }
  return recordToken === targetToken;
}

function activeLiveSessionRecordsForEngine(engine, sectionScope = null, cycleScope = null) {
  const archivedIds = new Set(
    loadArchivedSessionRecords()
      .map((item) => item?.sessionId)
      .filter(Boolean),
  );
  // normalizeCycleId/normalizeSectionId intentionally return fallback values
  // ("cycle" / "section") for blank input. Those fallbacks are useful for UI
  // labels, but they must never be used for conflict detection because two
  // missing metadata values would then look like the same operational slot.
  const rawTargetCycle = String(
    cycleScope?.cycleId || cycleScope?.value || cycleScope?.id || cycleScope?.name || "",
  ).trim();
  const rawTargetSection = String(
    sectionScope?.sectionId || sectionScope?.value || sectionScope?.id || sectionScope?.name || "",
  ).trim();
  const targetCycle = rawTargetCycle ? normalizeCycleId(rawTargetCycle) : "";
  const targetSection = rawTargetSection ? normalizeSectionId(rawTargetSection) : "";
  const targetIsFullSite =
    sectionScope?.fullSite === true ||
    ["full-site", "full-count", "all", "all-sections"].includes(targetSection);

  // A count cannot safely conflict until both operational dimensions have been
  // selected. This also prevents stale/legacy session records with missing
  // cycle or section metadata from opening the replace modal.
  if (!targetCycle || (!targetSection && !targetIsFullSite)) return [];

  return loadSessionRecords().filter((record) => {
    if (!record?.sessionId || archivedIds.has(record.sessionId)) return false;

    // Conflict detection must use the canonical active-session summary only.
    // Session seeds/details are intentionally excluded here because old detail
    // documents can survive archive/delete and were recreating "ghost" matches
    // even while the Sessions screen correctly showed an empty list.
    const hydrated = { ...(record || {}) };

    // Only sessions written by the current tenant-proof session pipeline are
    // eligible to block a new count. Legacy summaries are still recoverable from
    // Sessions, but they may not trigger a destructive replace prompt.
    if (Number(hydrated.tenantProofVersion || 0) < 2) return false;

    if (
      hydrated.archived === true ||
      hydrated.archivedAt ||
      hydrated.archiveReason ||
      hydrated.archiveType ||
      String(hydrated.status || "").trim().toLowerCase() === "archived"
    ) return false;

    const status = String(hydrated.status || "").trim().toLowerCase();
    // Only a genuinely live, editable session can block preparation. Completed,
    // verified, finalized and legacy status-less records must never trigger the
    // replace/resume dialog.
    if (status !== "active") return false;
    if (!sameStore(hydrated, engine)) return false;

    // A live session only conflicts with the exact same operational slot:
    // same site + same cycle + same section/full-site scope.
    const rawRecordCycle = String(
      hydrated.cycleId || hydrated.cycleKey || hydrated.cycle?.cycleId ||
      hydrated.cycle?.id || hydrated.cycleName || hydrated.cycle?.name || "",
    ).trim();
    if (!rawRecordCycle) return false;
    const recordCycle = normalizeCycleId(rawRecordCycle);
    if (recordCycle !== targetCycle) return false;

    const rawRecordSection = String(
      hydrated.sectionId || hydrated.sectionName || hydrated.scopeName || "",
    ).trim();
    const recordSection = rawRecordSection ? normalizeSectionId(rawRecordSection) : "";
    const recordIsFullSite =
      hydrated.fullSite === true ||
      hydrated.isFullSite === true ||
      hydrated.fullCount === true ||
      ["full-site", "full-count", "all", "all-sections"].includes(recordSection);

    if (targetIsFullSite) return recordIsFullSite;
    if (recordIsFullSite || !recordSection) return false;
    return recordSection === targetSection;
  });
}

function ignoredInventoryKeys(row = {}) {
  const keys = [];
  const sku = clean(row?.sku);
  const sourceId = clean(row?.sourceId || row?.handle || row?.id);
  const product = clean(row?.product || row?.name || row?.title);
  const sourceCategory = clean(row?.sourceCategory || row?.category);

  if (sku) keys.push(`sku:${sku}`);
  if (sourceId) keys.push(`id:${sourceId}`);
  if (product) {
    // Product/category is the stable fallback for malformed CSV rows whose
    // generated row id can change after reprocessing or source hydration.
    keys.push(`product:${product}|${sourceCategory}`);
    keys.push(`product:${product}`);
  }
  return Array.from(new Set(keys.filter(Boolean)));
}

function ignoredInventoryKey(row = {}) {
  return ignoredInventoryKeys(row)[0] || "";
}

function rowReadiness(row, engine) {
  const category = clean(row?.category);
  const sourceCategory = clean(row?.sourceCategory);
  const categoryMissing =
    !category ||
    category === "__unmapped__" ||
    row?.isUnmappedCategory === true;
  const ownership = engine?.config?.categoryOwnership || {};
  const sectionId = ownership[category] || ownership[row?.category] || "";
  const groups = Array.isArray(engine?.config?.categoryGroups)
    ? engine.config.categoryGroups
    : [];
  const group = groups.find((item) =>
    (item?.outputs || []).some((output) => clean(output) === category),
  );
  const ignoredSkus = new Set(
    (engine?.config?.ignoredInventorySkus || []).map((item) => clean(item)),
  );
  const ignoredKeys = new Set(
    (engine?.config?.ignoredInventoryKeys || []).map((item) => clean(item)),
  );
  const ignored = Boolean(
    row?.ignoredFromCounts ||
    row?.isIgnored ||
    row?.ignoreFromCount ||
    ignoredSkus.has(clean(row?.sku || row?.product)) ||
    ignoredInventoryKeys(row).some((key) => ignoredKeys.has(clean(key))),
  );

  if (ignored) return { status: "ignored", reason: "Ignored from counts" };
  if (categoryMissing) {
    return {
      status: "category",
      reason: sourceCategory
        ? `No rule for ${row.sourceCategory}`
        : "No category rule",
    };
  }
  if (!sectionId) return { status: "section", reason: "No section ownership" };
  if (!group) return { status: "group", reason: "No Count Group" };
  return {
    status: "ready",
    reason: "Ready for counting",
    sectionId,
    groupName: group.name,
  };
}

function titleCase(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim();
}

function keywordForRow(row) {
  const source = clean(row?.sourceCategory || row?.product);
  const common = [
    "vape",
    "pre roll",
    "preroll",
    "edible",
    "gummy",
    "flower",
    "indoor",
    "accessor",
    "cartridge",
    "snack",
    "concentrate",
    "dab",
    "disposable",
  ];
  return (
    common.find((word) => source.includes(word)) ||
    source.split(/\s+/).filter((word) => word.length > 3)[0] ||
    source
  );
}

function suggestedCategoryForRow(row, engine) {
  const haystack = clean(`${row?.product || ""} ${row?.sourceCategory || ""}`);
  const outputs = Array.from(
    new Set(
      [
        ...(engine?.rules || []).map((rule) => clean(rule?.output)),
        ...(engine?.config?.categoryOrder || []).map(clean),
        ...Object.keys(engine?.config?.categoryOwnership || {}).map(clean),
      ].filter(Boolean),
    ),
  );
  const aliases = [
    ["vape", ["vape"]],
    ["pre rolls", ["pre roll", "preroll"]],
    ["edibles", ["edible", "gummy", "chocolate"]],
    ["accessories", ["accessor", "grinder", "bong", "paper"]],
    ["cartridges", ["cartridge"]],
    ["concentrates", ["concentrate", "dab", "wax", "shatter"]],
    ["flower", ["flower", "indoor", "greenhouse", "outdoor"]],
  ];
  for (const [preferred, terms] of aliases) {
    if (!terms.some((term) => haystack.includes(term))) continue;
    const exact = outputs.find(
      (output) =>
        output === preferred ||
        output.includes(preferred) ||
        preferred.includes(output),
    );
    if (exact) return exact;
  }
  return (
    outputs.find((output) =>
      output
        .split(/\s+/)
        .some((token) => token.length > 3 && haystack.includes(token)),
    ) || ""
  );
}

function buildSuggestion(row, engine) {
  const currentCategory =
    clean(row?.category) && clean(row?.category) !== "__unmapped__"
      ? clean(row.category)
      : "";
  const category = currentCategory || suggestedCategoryForRow(row, engine);
  const ownership = engine?.config?.categoryOwnership || {};
  const groups = normalizeCountGroups(engine?.config?.categoryGroups);
  const group =
    groups.find((item) => item.outputs.includes(category)) ||
    groups.find(
      (item) =>
        clean(item.name).includes(category) ||
        category.includes(clean(item.name)),
    ) ||
    (groups.length === 1 ? groups[0] : null);
  const knownSections = Array.from(
    new Set(Object.values(ownership).filter(Boolean)),
  );
  return {
    keyword: keywordForRow(row),
    category,
    sectionId: category
      ? ownership[category] ||
        (knownSections.length === 1 ? knownSections[0] : "")
      : "",
    groupName: group?.name || "",
    confidence: category ? 92 : 58,
  };
}


function mergeGlobalInventoryConfiguration(globalEngine = {}, scopedEngine = {}) {
  const globalConfig = globalEngine?.config || {};
  const scopedConfig = scopedEngine?.config || {};
  const globalMapping = globalEngine?.mapping || {};
  const scopedMapping = scopedEngine?.mapping || {};
  const hasSiteOverride = scopedConfig.categoryOverride === true;

  // CSV field mapping is configured once from All Sites and must remain the
  // authority for every site. Site snapshots may retain an older cost header
  // (for example supply_price on District Six), so only the site-specific SOH
  // selector is allowed to stay scoped.
  const inheritedMapping = {
    ...scopedMapping,
    product: globalMapping.product || scopedMapping.product,
    sku: globalMapping.sku || scopedMapping.sku,
    category: globalMapping.category || scopedMapping.category,
    cost: globalMapping.cost || scopedMapping.cost,
    site: globalMapping.site || scopedMapping.site,
    systemSoh: scopedMapping.systemSoh || globalMapping.systemSoh,
  };

  // A site that is marked as inherited must use the current All Sites
  // Inventory Configuration as the authority. Older site snapshots can still
  // contain embedded rules or Unit Intelligence values from before the site
  // was reverted. Those snapshot values are historical import metadata, not a
  // live site override, and must not continue transforming quantities.
  if (!hasSiteOverride) {
    return {
      ...globalEngine,
      ...scopedEngine,
      rules: Array.isArray(globalEngine?.rules) ? globalEngine.rules : [],
      mapping: inheritedMapping,
      config: {
        ...globalConfig,
        ...scopedConfig,
        categoryOrder: Array.isArray(globalConfig.categoryOrder) ? globalConfig.categoryOrder : [],
        categoryGroups: normalizeCountGroups(globalConfig.categoryGroups),
        categoryOwnership: { ...(globalConfig.categoryOwnership || {}) },
        unitLibrary: Array.isArray(globalConfig.unitLibrary) ? globalConfig.unitLibrary : scopedConfig.unitLibrary,
        defaultUnitRule: globalConfig.defaultUnitRule || scopedConfig.defaultUnitRule,
        categoryUnitRules: { ...(globalConfig.categoryUnitRules || {}) },
        // Preserve the site's identity while making inheritance explicit.
        storeName: scopedConfig.storeName || globalConfig.storeName,
        categoryOverride: false,
      },
    };
  }

  const ruleKey = (rule = {}) => `${clean(rule.type)}::${clean(rule.value)}`;
  const mergedRuleMap = new Map();
  (globalEngine?.rules || []).forEach((rule) => mergedRuleMap.set(ruleKey(rule), rule));
  (scopedEngine?.rules || []).forEach((rule) => mergedRuleMap.set(ruleKey(rule), rule));

  const groupMap = new Map();
  normalizeCountGroups(globalConfig.categoryGroups).forEach((group) => {
    groupMap.set(clean(group.name), group);
  });
  normalizeCountGroups(scopedConfig.categoryGroups).forEach((group) => {
    groupMap.set(clean(group.name), group);
  });

  const categoryOrder = [];
  [...(globalConfig.categoryOrder || []), ...(scopedConfig.categoryOrder || [])].forEach((item) => {
    if (item && !categoryOrder.some((existing) => clean(existing) === clean(item))) categoryOrder.push(item);
  });

  return {
    ...globalEngine,
    ...scopedEngine,
    rules: Array.from(mergedRuleMap.values()),
    mapping: inheritedMapping,
    config: {
      ...globalConfig,
      ...scopedConfig,
      categoryOrder,
      categoryGroups: Array.from(groupMap.values()),
      categoryOwnership: {
        ...(globalConfig.categoryOwnership || {}),
        ...(scopedConfig.categoryOwnership || {}),
      },
    },
  };
}
function normalizeCountGroups(groups = []) {
  return (Array.isArray(groups) ? groups : [])
    .map((group) => ({
      name: String(group?.name || "").trim(),
      outputs: Array.from(
        new Set(
          (Array.isArray(group?.outputs) ? group.outputs : [])
            .map((item) => clean(item))
            .filter(Boolean),
        ),
      ),
    }))
    .filter((group) => group.name);
}


function SohSearchableMultiSelect({ label, allLabel, options, selected, onChange, getKey, getLabel }) {
  const [query, setQuery] = useState("");
  const detailsRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleOutsidePointer = (event) => {
      const details = detailsRef.current;
      if (!details?.open) return;
      if (details.contains(event.target)) return;
      details.open = false;
      setQuery("");
    };
    const handleEscape = (event) => {
      if (event.key !== "Escape") return;
      const details = detailsRef.current;
      if (!details?.open) return;
      details.open = false;
      setQuery("");
    };
    document.addEventListener("pointerdown", handleOutsidePointer, true);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointer, true);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const filtered = useMemo(() => {
    const needle = clean(query);
    if (!needle) return options;
    return options.filter((option) => clean(getLabel(option)).includes(needle));
  }, [options, query, getLabel]);

  const focusSearch = (seed = "") => {
    if (detailsRef.current) detailsRef.current.open = true;
    if (seed) setQuery(seed);
    requestAnimationFrame(() => searchRef.current?.focus());
  };

  return (
    <div className="mc-sohMultiSelect">
      <span>{label}</span>
      <details
        ref={detailsRef}
        onToggle={(event) => {
          if (event.currentTarget.open) requestAnimationFrame(() => searchRef.current?.focus());
          else setQuery("");
        }}
      >
        <summary
          onKeyDown={(event) => {
            if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
              event.preventDefault();
              focusSearch(event.key);
            }
          }}
        >
          <span>{selected.length ? `${selected.length} selected` : allLabel}</span>
        </summary>
        <div className="mc-sohMultiSelect__menu">
          <input
            ref={searchRef}
            type="search"
            value={query}
            placeholder={`Search ${label.toLowerCase()}…`}
            aria-label={`Search ${label}`}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button type="button" className="mc-sohMultiSelect__all" onClick={() => onChange([])}>
            {allLabel}
          </button>
          <div className="mc-sohMultiSelect__options">
            {filtered.length ? filtered.map((option) => {
              const key = getKey(option);
              return (
                <label key={key}>
                  <input
                    type="checkbox"
                    checked={selectedSet.has(key)}
                    onChange={() => onChange(selectedSet.has(key) ? selected.filter((value) => value !== key) : [...selected, key])}
                  />
                  <span>{getLabel(option)}</span>
                </label>
              );
            }) : <div className="mc-sohMultiSelect__empty">No matches</div>}
          </div>
        </div>
      </details>
    </div>
  );
}

export default function MorpheusCountPreparedData() {
  const sharedHydrationPausedRef = useRef(false);
  const currentProfile = useCurrentMcProfile();
  const adminStoreView = useAdminStoreView();
  // Admin site selection is reactive state. Derive the Review scope directly
  // from that state so changing the shared site selector rehydrates the active
  // inventory snapshot immediately instead of leaving the previous site's
  // readiness totals mounted until this route is remounted.
  const activeStoreScope = isAdminProfile(currentProfile)
    ? (adminStoreView || "ALL")
    : getActiveStoreScope(currentProfile);
  // Review can mount before the profile / organisation identity has fully
  // settled after a hard refresh or hot reload. The selected scope can still
  // already be "ALL", so depending only on activeStoreScope means the hydration
  // effect may never rerun once the real profile becomes available. Keep a
  // compact identity token in the dependency set so the authoritative snapshot
  // gets one fresh hydration pass when that identity resolves.
  const hydrationIdentity = [
    currentProfile?.profileId || currentProfile?.id || "",
    currentProfile?.orgId || currentProfile?.organisationId || currentProfile?.organizationId || "",
    currentProfile?.email || "",
  ].join("|");
  // C1: Storage revisions are keyed by the canonical site authority, while the
  // shared selector may hold a display/store alias. Review must read the same
  // ledger key Global Distribution writes or it can legitimately load an older
  // history even after the latest site revision exists.
  const storageAuthorityScope = useMemo(() => {
    const raw = String(activeStoreScope || "ALL").trim() || "ALL";
    if (raw.toUpperCase() === "ALL") return "ALL";
    try {
      const site = resolveSite(raw);
      return String(site?.siteId || raw).trim() || raw;
    } catch (_) {
      return raw;
    }
  }, [activeStoreScope]);
  const [engine, setEngine] = useState(() => {
    const cached = peekActiveInventorySnapshot(activeStoreScope);
    const stored =
      loadLatestImportEngineState(activeStoreScope) ||
      buildEmptyState(activeStoreScope);
    const globalEngine = String(activeStoreScope || "ALL").toUpperCase() === "ALL"
      ? stored
      : (loadEngineState("ALL") || buildEmptyState("ALL"));
    return cached && Array.isArray(cached.rawRows) && cached.rawRows.length
      ? mergeGlobalInventoryConfiguration(globalEngine, {
          ...stored,
          ...cached,
          mapping: cached?.mapping || stored?.mapping,
        })
      : mergeGlobalInventoryConfiguration(globalEngine, buildEmptyState(activeStoreScope));
  });
  const [snapshotLoading, setSnapshotLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showProducts, setShowProducts] = useState(false);
  const [reviewSubTab, setReviewSubTab] = useState("review");
  const [sohGroupsSelected, setSohGroupsSelected] = useState([]);
  const [sohProductsSelected, setSohProductsSelected] = useState([]);
  const [sohSearch, setSohSearch] = useState("");
  const [sohSiteRows, setSohSiteRows] = useState({});
  const [sohLoading, setSohLoading] = useState(false);
  const [sohMetricFilter, setSohMetricFilter] = useState("all");
  const [sohSortBy, setSohSortBy] = useState("group");
  const [sohSortDirection, setSohSortDirection] = useState("asc");
  const [sohSiteVersion, setSohSiteVersion] = useState(0);
  const [latestStorageRevision, setLatestStorageRevision] = useState(null);
  const [overwritePrompt, setOverwritePrompt] = useState(false);
  const [overwriteMatches, setOverwriteMatches] = useState([]);
  const [prepareSitePrompt, setPrepareSitePrompt] = useState(false);
  const [prepareSiteScope, setPrepareSiteScope] = useState("");
  const [pendingPrepareScope, setPendingPrepareScope] = useState("");
  const [sessionPreparing, setSessionPreparing] = useState(false);
  const [cycles, setCycles] = useState(() => activeCycles());
  const [sections, setSections] = useState(() => activeSections());
  const allowedSectionIds = useMemo(() => {
    if (!currentProfile || isAdminProfile(currentProfile)) return ["ALL"];
    return getProfileAllowedSectionIds(currentProfile, sections).map(normalizeSectionId).filter(Boolean);
  }, [currentProfile, sections]);
  const hasAllSectionAccess = useMemo(
    () => profileHasAllSectionAccess(currentProfile, sections),
    [currentProfile, sections],
  );
  const [selectedCycle, setSelectedCycle] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productPickerSearch, setProductPickerSearch] = useState("");
  const [reviewNotice, setReviewNotice] = useState("");
  const [exceptionSearch, setExceptionSearch] = useState("");
  const [exceptionStatus, setExceptionStatus] = useState("all");
  const [exceptionConfidence, setExceptionConfidence] = useState("all");
  const [patternFilter, setPatternFilter] = useState("");
  const [selectedPatternKeys, setSelectedPatternKeys] = useState([]);
  const [bulkPatternItems, setBulkPatternItems] = useState([]);
  const [bulkLearnOpen, setBulkLearnOpen] = useState(false);
  const [bulkRuleDrafts, setBulkRuleDrafts] = useState({});
  const [expandedBulkPattern, setExpandedBulkPattern] = useState("");
  const [bulkLearnStage, setBulkLearnStage] = useState("configure");
  const [bulkLearnProgress, setBulkLearnProgress] = useState([]);
  const [bulkLearnError, setBulkLearnError] = useState("");
  const [bulkCommitResult, setBulkCommitResult] = useState(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkTransactionId, setBulkTransactionId] = useState("");
  const bulkLearnCancelRef = useRef(false);
  const bulkLearnModalRef = useRef(null);
  const [bulkInitiallySelectedKeys, setBulkInitiallySelectedKeys] = useState(
    [],
  );
  const [ruleModal, setRuleModal] = useState(null);
  const [duplicateReviewOpen, setDuplicateReviewOpen] = useState(false);
  const [duplicateDecisionSaving, setDuplicateDecisionSaving] = useState(false);
  const [ruleDraft, setRuleDraft] = useState({
    category: "",
    sectionMode: "existing",
    sectionId: "",
    sectionName: "",
    groupMode: "existing",
    groupName: "",
  });
  const [undoEngine, setUndoEngine] = useState(null);
  const hydrateRunningRef = useRef(false);
  const hydrateQueuedRef = useRef(false);
  const hydrationWatchdogRef = useRef(null);
  const initialHydrationCompleteRef = useRef(false);
  const hydrationGenerationRef = useRef(0);
  const preparedEventTimersRef = useRef(new Map());
  const navigate = useNavigate();

  // MC490 — Development-only page traffic scope. It observes existing Review
  // Firestore activity only and never adds a network operation.

  const deferPreparedEvent = (key, callback) => {
    const timers = preparedEventTimersRef.current;
    const existing = timers.get(key);
    if (existing) window.clearTimeout(existing);
    const timer = window.setTimeout(() => {
      timers.delete(key);
      callback();
    }, 0);
    timers.set(key, timer);
  };

  const scrollToReviewSection = (id) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const target = document.getElementById(id);
        if (!target) return;
        const offset = 190;
        const top = target.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      });
    });
  };

  useEffect(() => {
    let cancelled = false;
    loadStorageMovementLedger(storageAuthorityScope)
      .then((entries) => {
        if (cancelled) return;
        setLatestStorageRevision(Array.isArray(entries) && entries.length ? entries[0] : null);
      })
      .catch(() => {
        if (!cancelled) setLatestStorageRevision(null);
      });
    return () => {
      cancelled = true;
    };
  }, [storageAuthorityScope, engine?.updatedAt, engine?.lastGeneratedAt]);

  const prepareSiteOptions = useMemo(() => {
    // Preparing a count must use the same site authority as All Sites inventory.
    // The durable Sites registry can hydrate later (or temporarily contain only a
    // row-site subset), while the active ALL snapshot already carries every
    // inventory_<site> column. Merge both sources so the picker is complete as
    // soon as Review is ready and never collapses to one site after refresh.
    const scopes = [];
    const addScope = (value) => {
      const scope = normalizeStoreValue(value || "");
      if (!scope || scope === "ALL" || scopes.includes(scope)) return;
      scopes.push(scope);
    };

    detectSiteHeaders(engine?.parsedHeaders || []).forEach(addScope);
    if (Array.isArray(engine?.rawRows) && engine.rawRows.length) {
      detectSiteHeaders(Object.keys(engine.rawRows[0] || {})).forEach(addScope);
    }
    loadSites()
      .filter((site) => site?.active !== false)
      .forEach((site) => addScope(site?.storeKey || site?.siteId || site?.name || ""));

    return scopes.sort((a, b) => formatStoreLabel(a).localeCompare(formatStoreLabel(b)));
  }, [adminStoreView, engine?.updatedAt, engine?.lastGeneratedAt, engine?.parsedHeaders, engine?.rawRows]);

  useEffect(() => {
    let cancelled = false;
    let scopeHydrationRetryTimer = null;
    const generation = ++hydrationGenerationRef.current;

    const hydrate = async ({ foreground = false } = {}) => {
      if (sharedHydrationPausedRef.current) return;
      if (hydrateRunningRef.current) {
        hydrateQueuedRef.current = true;
        return;
      }
      hydrateRunningRef.current = true;
      if (foreground || !initialHydrationCompleteRef.current) {
        setSnapshotLoading(true);
        window.clearTimeout(hydrationWatchdogRef.current);
        hydrationWatchdogRef.current = window.setTimeout(() => {
          // A slow/offline cloud read must never leave Inventory Review blocked.
          if (cancelled || generation !== hydrationGenerationRef.current) return;
          initialHydrationCompleteRef.current = true;
          setSnapshotLoading(false);
        }, 7000);
      }
      try {
        // Learned/category configuration and duplicate decisions are compact
        // shared authorities. Hydrate them explicitly before resolving the
        // local engine so another browser's Learn Product decision is visible
        // immediately without depending on a stale/oversized full-engine mirror.
        await hydrateCategoryConfigurationFromCloud(activeStoreScope).catch(() => null);
        const duplicateDecisions = await hydrateDuplicateReviewDecisions(activeStoreScope);
        if (cancelled || generation !== hydrationGenerationRef.current) return;
        const storedBase =
          loadLatestImportEngineState(activeStoreScope) ||
          buildEmptyState(activeStoreScope);
        // The compact duplicate ledger is the authority for duplicate review.
        // Apply it explicitly after hydration instead of relying on whichever
        // engine/snapshot document happened to hydrate last. Large imports can
        // keep an older engine config in Firestore, which previously redrew the
        // 19 duplicate groups after refresh even though the compact ledger had
        // saved successfully.
        const stored = {
          ...storedBase,
          config: {
            ...(storedBase?.config || {}),
            acceptedDuplicateKeys: Array.isArray(duplicateDecisions?.acceptedDuplicateKeys)
              ? duplicateDecisions.acceptedDuplicateKeys
              : (storedBase?.config?.acceptedDuplicateKeys || []),
            ignoredInventorySkus: Array.isArray(duplicateDecisions?.ignoredInventorySkus)
              ? duplicateDecisions.ignoredInventorySkus
              : (storedBase?.config?.ignoredInventorySkus || []),
            ignoredInventoryKeys: Array.isArray(duplicateDecisions?.ignoredInventoryKeys)
              ? duplicateDecisions.ignoredInventoryKeys
              : (storedBase?.config?.ignoredInventoryKeys || []),
          },
        };
        let snapshot =
          peekActiveInventorySnapshot(activeStoreScope) ||
          (await loadActiveInventorySnapshot(activeStoreScope));
        if (cancelled || generation !== hydrationGenerationRef.current) return;

        // A lightweight engine can legitimately arrive before its chunked active
        // snapshot on route refresh / profile resolution. Treat one null result
        // as a hydration miss, not as proof that the inventory was deleted. If
        // this scope already records a ready import reference, perform one bounded
        // retry. This is deliberately not polling: a true empty scope pays at
        // most one extra pointer read, while a transient identity/cache race
        // self-heals without flashing "No active inventory snapshot yet."
        const expectedSnapshot =
          String(storedBase?.importSnapshotRef?.status || "").toLowerCase() === "ready" &&
          Number(storedBase?.importSnapshotRef?.rowCount || 0) > 0;
        if (!snapshot && expectedSnapshot) {
          await new Promise((resolve) => window.setTimeout(resolve, 300));
          if (cancelled || generation !== hydrationGenerationRef.current) return;
          snapshot =
            peekActiveInventorySnapshot(activeStoreScope) ||
            (await loadActiveInventorySnapshot(activeStoreScope));
          if (cancelled || generation !== hydrationGenerationRef.current) return;
        }
        const globalEngine = String(activeStoreScope || "ALL").toUpperCase() === "ALL"
          ? stored
          : (loadEngineState("ALL") || buildEmptyState("ALL"));
        if (snapshot && Array.isArray(snapshot.rawRows) && snapshot.rawRows.length) {
          const mergedEngine = mergeGlobalInventoryConfiguration(globalEngine, {
            ...stored,
            ...snapshot,
            // Inventory decisions belong to the durable engine configuration,
            // not to the intake snapshot. A snapshot can be older than the
            // latest Review decision and must never overwrite accepted
            // duplicate keys, ignored SKUs, learned rules, ownership or groups.
            config: stored?.config || snapshot?.config,
            rules: Array.isArray(stored?.rules) ? stored.rules : (snapshot?.rules || []),
            mapping: snapshot?.mapping || stored?.mapping,
          });
          setEngine({
            ...mergedEngine,
            config: {
              ...(mergedEngine?.config || {}),
              acceptedDuplicateKeys: stored.config.acceptedDuplicateKeys,
              ignoredInventorySkus: stored.config.ignoredInventorySkus,
              ignoredInventoryKeys: stored.config.ignoredInventoryKeys,
            },
          });
        } else {
          const mergedEngine = mergeGlobalInventoryConfiguration(globalEngine, {
            ...stored,
            rawRows: [],
            preparedRows: [],
            parsedHeaders: [],
          });
          setEngine({
            ...mergedEngine,
            config: {
              ...(mergedEngine?.config || {}),
              acceptedDuplicateKeys: stored.config.acceptedDuplicateKeys,
              ignoredInventorySkus: stored.config.ignoredInventorySkus,
              ignoredInventoryKeys: stored.config.ignoredInventoryKeys,
            },
          });
        }
        initialHydrationCompleteRef.current = true;
      } finally {
        hydrateRunningRef.current = false;
        window.clearTimeout(hydrationWatchdogRef.current);
        if (!cancelled && generation === hydrationGenerationRef.current) setSnapshotLoading(false);
        if (!cancelled && generation === hydrationGenerationRef.current && hydrateQueuedRef.current && !sharedHydrationPausedRef.current) {
          hydrateQueuedRef.current = false;
          // Coalesce bursts of cloud/local events into one quiet background pass.
          window.setTimeout(() => hydrate({ foreground: false }), 150);
        } else {
          hydrateQueuedRef.current = false;
        }
      }
    };

    let unsubscribeShared = () => {};
    const connectSharedSnapshot = () => {
      try { unsubscribeShared(); } catch (_) {}
      unsubscribeShared = subscribeActiveInventorySnapshot(
        activeStoreScope,
        () => deferPreparedEvent("shared-snapshot", () => hydrate({ foreground: false })),
        (err) => console.warn("Shared active inventory subscription failed", err),
      );
    };

    const requestScopeHydration = ({ foreground = false } = {}) => {
      if (cancelled || generation !== hydrationGenerationRef.current) return;

      // A site change can arrive while the previous scope is still finishing an
      // async hydration. The shared running guard deliberately prevents two
      // Firestore hydration passes from overlapping, but the old behaviour could
      // also swallow the *new* scope request: the previous generation finished,
      // discarded its queued work, and Review kept rendering the old site's
      // snapshot until the operator pressed the manual refresh button.
      //
      // Wait for the in-flight generation to release the guard, then hydrate the
      // latest selected scope exactly once. This preserves the cost-saving
      // single-flight behaviour while making the global site selector reactive.
      if (hydrateRunningRef.current) {
        if (foreground) setSnapshotLoading(true);
        if (scopeHydrationRetryTimer !== null) return;
        const retry = () => {
          scopeHydrationRetryTimer = null;
          if (cancelled || generation !== hydrationGenerationRef.current) return;
          if (hydrateRunningRef.current) {
            scopeHydrationRetryTimer = window.setTimeout(retry, 50);
            return;
          }
          hydrate({ foreground });
        };
        scopeHydrationRetryTimer = window.setTimeout(retry, 25);
        return;
      }

      hydrate({ foreground });
    };

    initialHydrationCompleteRef.current = false;
    // Immediately mark the new scope as loading so stale All Sites/site totals
    // are never presented as the selected site's current Review snapshot.
    setSnapshotLoading(true);
    requestScopeHydration({ foreground: true });
    connectSharedSnapshot();
    const requestBackgroundHydration = () => {
      if (sharedHydrationPausedRef.current) return;
      deferPreparedEvent("background-hydration", () => {
        if (sharedHydrationPausedRef.current) return;
        hydrate({ foreground: false });
      });
    };
    const onActiveInventoryChange = requestBackgroundHydration;
    const onSyncReady = () => {
      deferPreparedEvent("sync-ready", () => {
        requestBackgroundHydration();
        connectSharedSnapshot();
      });
    };
    const onSharedReviewChange = requestBackgroundHydration;
    window.addEventListener(MC_ACTIVE_INVENTORY_EVENT, onActiveInventoryChange);
    window.addEventListener(MC_SYNC_READY_EVENT, onSyncReady);
    // Inventory Review is derived from two shared sources: the active snapshot
    // and Inventory Configuration. Rehydrate whenever cloud sync or the engine
    // source of truth changes so another reviewer's learned rule, ignore,
    // ownership or grouping decision is reflected without a page refresh.
    window.addEventListener("mc-cloud-sync", onSharedReviewChange);
    window.addEventListener(MC_ENGINE_EVENT, onSharedReviewChange);
    window.addEventListener(MC_SECTION_EVENT, onSharedReviewChange);
    return () => {
      cancelled = true;
      if (generation === hydrationGenerationRef.current) {
        window.clearTimeout(hydrationWatchdogRef.current);
        setSnapshotLoading(false);
      }
      if (scopeHydrationRetryTimer !== null) window.clearTimeout(scopeHydrationRetryTimer);
      try { unsubscribeShared(); } catch (_) {}
      window.removeEventListener(MC_ACTIVE_INVENTORY_EVENT, onActiveInventoryChange);
      window.removeEventListener(MC_SYNC_READY_EVENT, onSyncReady);
      window.removeEventListener("mc-cloud-sync", onSharedReviewChange);
      window.removeEventListener(MC_ENGINE_EVENT, onSharedReviewChange);
      window.removeEventListener(MC_SECTION_EVENT, onSharedReviewChange);
    };
  }, [activeStoreScope, hydrationIdentity]);

  useEffect(() => {
    const syncCycles = () => deferPreparedEvent("cycles", () => setCycles(activeCycles()));
    const syncSections = () => deferPreparedEvent("sections", () => setSections(activeSections()));
    setCycles(activeCycles());
    setSections(activeSections());
    window.addEventListener(MC_CYCLE_EVENT, syncCycles);
    window.addEventListener(MC_SECTION_EVENT, syncSections);
    window.addEventListener("mc-cloud-sync", syncCycles);
    window.addEventListener("mc-cloud-sync", syncSections);
    return () => {
      window.removeEventListener(MC_CYCLE_EVENT, syncCycles);
      window.removeEventListener(MC_SECTION_EVENT, syncSections);
      window.removeEventListener("mc-cloud-sync", syncCycles);
      window.removeEventListener("mc-cloud-sync", syncSections);
    };
  }, []);

  const effectiveReviewMapping = useMemo(() => {
    const resolvedSystemSoh = resolveInventoryHeaderForScope(
      activeStoreScope,
      engine.parsedHeaders || [],
      engine,
    );
    return {
      ...(engine.mapping || {}),
      ...(resolvedSystemSoh ? { systemSoh: resolvedSystemSoh } : {}),
    };
  }, [activeStoreScope, engine.parsedHeaders, engine.rawRows, engine.mapping, engine.config?.storeName, engine.config?.dataSourceMode]);

  const preparedRows = useMemo(
    () =>
      buildPreparedData(
        engine.rawRows || [],
        effectiveReviewMapping,
        engine.config,
        engine.rules,
      ),
    [engine.rawRows, effectiveReviewMapping, engine.config, engine.rules],
  );

  useEffect(() => {
    // Publish only compact identities from the complete site catalogue; picker
    // selections and staff section filters never narrow this authority.
    const site = resolveSite(activeStoreScope);
    if (snapshotLoading || !site?.siteId || activeStoreScope === 'ALL' || !hasAllSectionAccess || !preparedRows.length) return;
    const ownership = engine?.config?.categoryOwnership || {};
    const timer = window.setTimeout(() => {
      mcSyncNeoCatalogue({siteId:site.siteId, products:preparedRows.map(row => ({
        id:row.id,sku:row.sku,product:row.product,category:row.category,
        sectionId:ownership[clean(row.category)] || ownership[row.category] || '',
      })),groups:engine?.config?.categoryGroups || []}).catch(error => console.warn('Neo count catalogue sync failed', error));
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [activeStoreScope,snapshotLoading,preparedRows,engine.config,hasAllSectionAccess]);

  const reviewedRows = useMemo(() => {
    const ownership = engine?.config?.categoryOwnership || {};
    return preparedRows
      .map((row) => ({
        ...row,
        readiness: rowReadiness(row, engine),
      }))
      .filter((row) => {
        if (hasAllSectionAccess) return true;
        const sectionId = normalizeSectionId(
          ownership?.[clean(row.category)] || ownership?.[row.category] || row?.sectionId || row?.sectionName || "",
        );
        return sectionId && allowedSectionIds.includes(sectionId);
      });
  }, [preparedRows, engine, hasAllSectionAccess, allowedSectionIds]);

  const unitRuleScopeLabel = (row = {}) => {
    const isAllSites = activeStoreScope === "ALL";
    const isSiteOverride = !isAllSites && engine?.config?.categoryOverride === true;
    const sourceName = row.unitRuleSource === "category"
      ? `category rule: ${row.unitRuleCategory || row.category || "category"}`
      : "default rule";

    if (isAllSites) return `Global ${sourceName}`;
    if (isSiteOverride) return `Site ${sourceName}`;
    return `Inherited All Sites ${sourceName}`;
  };

  const readiness = useMemo(() => {
    const counts = { ready: 0, category: 0, section: 0, group: 0, ignored: 0 };
    reviewedRows.forEach((row) => {
      const key = row.readiness?.status || "category";
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [reviewedRows]);

  const exceptionRows = useMemo(
    () =>
      reviewedRows.filter((row) =>
        ["category", "section", "group"].includes(row.readiness?.status),
      ),
    [reviewedRows],
  );
  const intelligenceScore = reviewedRows.length
    ? Math.round(
        ((readiness.ready + readiness.ignored) / reviewedRows.length) * 100,
      )
    : 0;
  const learnedRuleCount = Array.isArray(engine?.rules)
    ? engine.rules.filter((rule) => clean(rule?.output)).length
    : 0;
  const outputCount = useMemo(() => {
    const values = new Set(
      [
        ...(engine?.rules || []).map((rule) => clean(rule?.output)),
        ...(engine?.config?.categoryOrder || []).map(clean),
        ...Object.keys(engine?.config?.categoryOwnership || {}).map(clean),
        ...(engine?.config?.categoryGroups || []).flatMap((group) =>
          (group?.outputs || []).map(clean),
        ),
      ].filter(Boolean),
    );
    return values.size;
  }, [engine]);
  const filteredExceptionRows = useMemo(() => {
    const needle = clean(exceptionSearch || patternFilter);
    return exceptionRows.filter((row) => {
      const suggestion = buildSuggestion(row, engine);
      if (
        exceptionStatus !== "all" &&
        row.readiness?.status !== exceptionStatus
      )
        return false;
      if (exceptionConfidence === "high" && suggestion.confidence < 90)
        return false;
      if (
        exceptionConfidence === "medium" &&
        (suggestion.confidence < 70 || suggestion.confidence >= 90)
      )
        return false;
      if (exceptionConfidence === "low" && suggestion.confidence >= 70)
        return false;
      if (!needle) return true;
      return [
        row.product,
        row.sku,
        row.sourceCategory,
        row.readiness?.reason,
        suggestion.keyword,
        suggestion.category,
        suggestion.groupName,
      ].some((value) => clean(value).includes(needle));
    });
  }, [
    exceptionRows,
    engine,
    exceptionSearch,
    patternFilter,
    exceptionStatus,
    exceptionConfidence,
  ]);
  const patternSuggestions = useMemo(() => {
    const buckets = new Map();
    exceptionRows.forEach((row) => {
      const suggestion = buildSuggestion(row, engine);
      const key = `${row.readiness?.status}:${suggestion.keyword}:${suggestion.category}`;
      if (!buckets.has(key))
        buckets.set(key, {
          ...suggestion,
          status: row.readiness?.status,
          rows: [],
        });
      buckets.get(key).rows.push(row);
    });
    return Array.from(buckets.values())
      .filter((item) => item.rows.length >= 2)
      .sort((a, b) => b.rows.length - a.rows.length)
      .slice(0, 6);
  }, [exceptionRows, engine]);

  const patternKey = (item) =>
    `${item.status}:${item.keyword}:${item.category}`;
  const selectedPatterns = useMemo(
    () =>
      bulkLearnOpen && bulkPatternItems.length
        ? bulkPatternItems
        : patternSuggestions.filter((item) =>
            selectedPatternKeys.includes(patternKey(item)),
          ),
    [patternSuggestions, selectedPatternKeys, bulkLearnOpen, bulkPatternItems],
  );
  const selectedPatternProducts = useMemo(
    () =>
      new Set(
        selectedPatterns.flatMap((item) => item.rows.map((row) => row.id)),
      ).size,
    [selectedPatterns],
  );
  const allPatternsSelected =
    patternSuggestions.length > 0 &&
    selectedPatterns.length === patternSuggestions.length;
  const projectedReadiness = reviewedRows.length
    ? Math.min(
        100,
        Math.round(
          ((readiness.ready + readiness.ignored + selectedPatternProducts) /
            reviewedRows.length) *
            100,
        ),
      )
    : intelligenceScore;

  useEffect(() => {
    const available = new Set(patternSuggestions.map(patternKey));
    setSelectedPatternKeys((current) =>
      current.filter((key) => available.has(key)),
    );
  }, [patternSuggestions]);

  const togglePatternSelection = (item) => {
    const key = patternKey(item);
    setSelectedPatternKeys((current) =>
      current.includes(key)
        ? current.filter((value) => value !== key)
        : [...current, key],
    );
  };

  const removePatternFromBulkLearn = (item) => {
    const key = patternKey(item);
    setSelectedPatternKeys((current) =>
      current.filter((value) => value !== key),
    );
    setBulkRuleDrafts((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setExpandedBulkPattern((current) => (current === key ? "" : current));
  };

  const toggleAllPatterns = () => {
    setSelectedPatternKeys(
      allPatternsSelected ? [] : patternSuggestions.map(patternKey),
    );
  };

  const openBulkLearn = (patternsOverride = null) => {
    const patterns = Array.isArray(patternsOverride) && patternsOverride.length
      ? patternsOverride
      : selectedPatterns;
    if (!patterns.length) return;
    setBulkPatternItems(patterns);
    const drafts = {};
    patterns.forEach((item) => {
      const key = patternKey(item);
      const fallbackName = titleCase(
        item.keyword || item.category || "New Output",
      );
      const isHighConfidence = Number(item.confidence || 0) >= 70;
      const existingOutput = knownOutputOptions.find(
        (output) => clean(output) === clean(item.category),
      );
      drafts[key] = {
        outputMode: existingOutput ? "existing" : "new",
        output: existingOutput || titleCase(item.category || fallbackName),
        sectionMode: item.sectionId && isHighConfidence ? "existing" : "new",
        sectionId: item.sectionId && isHighConfidence ? item.sectionId : "",
        sectionName: item.sectionId && isHighConfidence ? "" : fallbackName,
        groupMode: item.groupName && isHighConfidence ? "existing" : "new",
        groupName:
          item.groupName && isHighConfidence ? item.groupName : fallbackName,
      };
    });
    setBulkRuleDrafts(drafts);
    const keys = patterns.map(patternKey);
    setSelectedPatternKeys(keys);
    setBulkInitiallySelectedKeys(keys);
    // Start bulk learning with every pattern collapsed so the operator first
    // sees the full decision set before choosing a pattern to expand.
    setExpandedBulkPattern("");
    setBulkLearnStage("configure");
    setBulkLearnProgress([]);
    setBulkLearnError("");
    setBulkCommitResult(null);
    setBulkLearnOpen(true);
  };

  const updateBulkRuleDraft = (key, patch) => {
    setBulkRuleDrafts((current) => ({
      ...current,
      [key]: { ...(current[key] || {}), ...patch },
    }));
  };

  useEffect(() => {
    if (!bulkLearnOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setBulkLearnOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [bulkLearnOpen]);

  // Every bulk-learning step opens from the top. The modal owns its own scroll
  // container, so stage changes must reset that container rather than relying
  // on the page/window scroll position.
  useEffect(() => {
    if (!bulkLearnOpen) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const modal = bulkLearnModalRef.current;
      if (modal) modal.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [bulkLearnOpen, bulkLearnStage]);

  const commitInventoryDecision = (nextEngine, message, allowUndo = true) => {
    if (allowUndo) setUndoEngine(engine);

    // Pause hydration before writing any local state. Ignore/duplicate decisions
    // live in a compact ledger because the full inventory engine can exceed the
    // Firestore document limit. Persist that ledger first so an engine-change
    // event can never rehydrate an older decision over the user's click.
    sharedHydrationPausedRef.current = true;
    saveDuplicateReviewDecisions(nextEngine, activeStoreScope).catch((error) => {
      console.error("Failed to persist compact inventory decision ledger", error);
    });
    saveEngineState(nextEngine, activeStoreScope);
    setEngine(nextEngine);
    setReviewNotice(message);

    const releaseHydration = window.setTimeout(() => {
      sharedHydrationPausedRef.current = false;
    }, 6000);

    saveEngineStateDurable(nextEngine, activeStoreScope)
      .catch((error) => {
        console.error("Failed to persist inventory review decision", error);
        setReviewNotice("The review decision was saved on this device, but cloud confirmation timed out. It will retry when sync reconnects.");
      })
      .finally(() => {
        window.clearTimeout(releaseHydration);
        sharedHydrationPausedRef.current = false;
      });

    window.setTimeout(() => setReviewNotice(""), 30000);
  };

  const openRuleModal = (rows, suggestion) => {
    const normalizedSuggestion = {
      status: suggestion?.status || rows?.[0]?.readiness?.status || "category",
      keyword: suggestion?.keyword || keywordForRow(rows?.[0]),
      category: suggestion?.category || "",
      sectionId: suggestion?.sectionId || "",
      groupName: suggestion?.groupName || "",
      confidence: Number(suggestion?.confidence || 58),
      rows: Array.isArray(rows) ? rows : [],
    };
    openBulkLearn([normalizedSuggestion]);
  };

  const saveRuleFromModal = () => {
    if (!ruleModal?.rows?.length || !clean(ruleDraft.category)) return;
    const category = clean(ruleDraft.category);
    let sectionId = ruleDraft.sectionId;
    if (ruleDraft.sectionMode === "new") {
      sectionId = normalizeSectionId(
        ruleDraft.sectionName || ruleDraft.category,
      );
      const current = activeSections();
      if (
        !current.some(
          (item) =>
            normalizeSectionId(item.sectionId || item.id || item.name) ===
            sectionId,
        )
      ) {
        saveSections([
          ...current,
          {
            id: sectionId,
            sectionId,
            name: ruleDraft.sectionName || titleCase(category),
            active: true,
          },
        ]);
        setSections(activeSections());
      }
    }
    const groupName = String(ruleDraft.groupName || "").trim();
    const nextRules = [...(engine.rules || [])];
    const keyword = clean(ruleModal.suggestion?.keyword);
    if (
      keyword &&
      !nextRules.some(
        (rule) =>
          clean(rule.type) === "contains" &&
          clean(rule.value) === keyword &&
          clean(rule.output) === category,
      )
    ) {
      nextRules.push({ type: "contains", value: keyword, output: category });
    }
    const nextConfig = { ...(engine.config || {}) };

    // MC494 — learning an ignored product must also remove its explicit Ignore
    // decision. Previously the reusable rule was created but rowReadiness()
    // still short-circuited on ignoredInventorySkus/ignoredInventoryKeys, so a
    // product could remain in the Ignored workspace after being taught.
    const learnedSkus = new Set((ruleModal.rows || []).map((row) => clean(row?.sku)).filter(Boolean));
    const learnedKeys = new Set((ruleModal.rows || []).flatMap((row) => ignoredInventoryKeys(row).map(clean)).filter(Boolean));
    nextConfig.ignoredInventorySkus = (nextConfig.ignoredInventorySkus || []).filter((item) => !learnedSkus.has(clean(item)));
    nextConfig.ignoredInventoryKeys = (nextConfig.ignoredInventoryKeys || []).filter((item) => !learnedKeys.has(clean(item)));

    nextConfig.categoryOrder = Array.from(
      new Set([...(nextConfig.categoryOrder || []), category]),
    );
    if (sectionId)
      nextConfig.categoryOwnership = {
        ...(nextConfig.categoryOwnership || {}),
        [category]: sectionId,
      };
    let groups = normalizeCountGroups(nextConfig.categoryGroups);
    if (groupName) {
      const existing = groups.find(
        (group) => clean(group.name) === clean(groupName),
      );
      groups = existing
        ? groups.map((group) =>
            clean(group.name) === clean(groupName)
              ? {
                  ...group,
                  outputs: Array.from(new Set([...group.outputs, category])),
                }
              : group,
          )
        : [...groups, { name: groupName, outputs: [category] }];
      nextConfig.categoryGroups = groups;
    }
    const learnedEngine = { ...engine, rules: nextRules, config: nextConfig };
    commitInventoryDecision(
      learnedEngine,
      `Rule learned. ${ruleModal.rows.length} product${ruleModal.rows.length === 1 ? "" : "s"} updated.`,
    );

    // Learn Product is a global teaching action, not a browser-local/site-only
    // hint. Persist the compact learned configuration to All Sites and, when
    // relevant, the current site. This avoids shipping the full inventory
    // engine while making Rules/Groups/Ownership immediately consistent across
    // browsers and devices.
    const globalBase = loadEngineState("ALL") || buildEmptyState("ALL");
    const globalLearnedEngine = {
      ...globalBase,
      rules: nextRules,
      config: {
        ...(globalBase.config || {}),
        categoryOrder: nextConfig.categoryOrder || [],
        halfGramCategories: nextConfig.halfGramCategories || [],
        conversionCategories: nextConfig.conversionCategories || nextConfig.halfGramCategories || [],
        categoryGroups: nextConfig.categoryGroups || [],
        categoryOwnership: nextConfig.categoryOwnership || {},
        unitLibrary: nextConfig.unitLibrary || globalBase.config?.unitLibrary || [],
        defaultUnitRule: nextConfig.defaultUnitRule || globalBase.config?.defaultUnitRule || {},
        categoryUnitRules: nextConfig.categoryUnitRules || globalBase.config?.categoryUnitRules || {},
        categoryOverride: false,
      },
    };
    saveCategoryConfigurationDurable(globalLearnedEngine, "ALL").catch((error) => {
      console.error("Failed to persist learned global Inventory Configuration", error);
    });
    if (normalizeStoreValue(activeStoreScope || "ALL") !== "ALL") {
      saveCategoryConfigurationDurable(learnedEngine, activeStoreScope).catch((error) => {
        console.error("Failed to persist learned scoped Inventory Configuration", error);
      });
    }

    setRuleModal(null);
  };

  const undoLastDecision = () => {
    if (!undoEngine) return;
    saveEngineState(undoEngine, activeStoreScope);
    setEngine(undoEngine);
    setUndoEngine(null);
    setReviewNotice("Last inventory decision undone.");
  };

  const applySuggestion = (rows, suggestion, createReusableRule = true) => {
    if (!rows?.length) return;
    const nextRules = [...(engine.rules || [])];
    if (suggestion.category && createReusableRule && suggestion.keyword) {
      const exists = nextRules.some(
        (rule) =>
          clean(rule.type) === "contains" &&
          clean(rule.value) === clean(suggestion.keyword) &&
          clean(rule.output) === clean(suggestion.category),
      );
      if (!exists)
        nextRules.push({
          type: "contains",
          value: suggestion.keyword,
          output: clean(suggestion.category),
        });
    }
    const nextConfig = { ...(engine.config || {}) };
    if (suggestion.category && suggestion.sectionId) {
      nextConfig.categoryOwnership = {
        ...(nextConfig.categoryOwnership || {}),
        [clean(suggestion.category)]: suggestion.sectionId,
      };
    }
    if (suggestion.category && suggestion.groupName) {
      nextConfig.categoryGroups = (nextConfig.categoryGroups || []).map(
        (group) =>
          group.name === suggestion.groupName
            ? {
                ...group,
                outputs: Array.from(
                  new Set([
                    ...(group.outputs || []),
                    clean(suggestion.category),
                  ]),
                ),
              }
            : group,
      );
    }
    commitInventoryDecision(
      { ...engine, rules: nextRules, config: nextConfig },
      `${rows.length} product${rows.length === 1 ? "" : "s"} resolved. Future matching imports will follow this decision.`,
    );
  };

  const ignoreRows = (rows) => {
    const ignored = new Set((engine?.config?.ignoredInventorySkus || []).map(clean));
    const ignoredKeys = new Set((engine?.config?.ignoredInventoryKeys || []).map(clean));
    rows.forEach((row) => {
      const sku = clean(row?.sku);
      const keys = ignoredInventoryKeys(row).map(clean).filter(Boolean);
      if (sku) ignored.add(sku);
      keys.forEach((key) => ignoredKeys.add(key));
    });
    const nextEngine = {
      ...engine,
      config: {
        ...(engine.config || {}),
        ignoredInventorySkus: Array.from(ignored),
        ignoredInventoryKeys: Array.from(ignoredKeys),
      },
    };
    commitInventoryDecision(
      nextEngine,
      `${rows.length} product${rows.length === 1 ? "" : "s"} excluded from Morpheus counts.`,
    );
  };

  const restoreIgnoredRow = (row) => {
    const sku = clean(row?.sku);
    const keys = ignoredInventoryKeys(row).map(clean).filter(Boolean);
    const nextEngine = {
      ...engine,
      config: {
        ...(engine.config || {}),
        ignoredInventorySkus: (engine?.config?.ignoredInventorySkus || []).filter((item) => clean(item) !== sku),
        ignoredInventoryKeys: (engine?.config?.ignoredInventoryKeys || []).filter((item) => !keys.includes(clean(item))),
      },
    };
    commitInventoryDecision(nextEngine, `${row?.product || "Product"} returned to Inventory Review.`);
  };

  // MC479 — recovery action for an accidentally contaminated ignore ledger.
  // This uses the same durable decision path as restoring one product, but clears
  // only the ignore lists. Rules, outputs, mappings and inventory rows are untouched.
  const restoreAllIgnoredRows = async () => {
    if (!ignoredRows.length || duplicateDecisionSaving) return;
    const count = ignoredRows.length;
    const nextEngine = {
      ...engine,
      config: {
        ...(engine.config || {}),
        ignoredInventorySkus: [],
        ignoredInventoryKeys: [],
      },
    };

    // Clearing the ignore ledger is a destructive removal operation, so wait
    // for the compact authority to confirm the replacement before claiming
    // success. The old union-style save could never delete existing entries.
    setDuplicateDecisionSaving(true);
    sharedHydrationPausedRef.current = true;
    setUndoEngine(engine);
    saveEngineState(nextEngine, activeStoreScope);
    setEngine(nextEngine);
    try {
      const saved = await saveDuplicateReviewDecisions(nextEngine, activeStoreScope);
      if (!saved) throw new Error("Ignore-ledger replacement timed out.");
      setReviewNotice(`${count} ignored products returned to Inventory Review.`);
      saveEngineStateDurable(nextEngine, activeStoreScope).catch(() => {});
    } catch (error) {
      console.error("Failed to clear ignored inventory ledger", error);
      setEngine(engine);
      saveEngineState(engine, activeStoreScope);
      setReviewNotice("The ignored products could not be confirmed as restored. Nothing was reported as successful; please try again.");
    } finally {
      setDuplicateDecisionSaving(false);
      sharedHydrationPausedRef.current = false;
      window.setTimeout(() => setReviewNotice(""), 30000);
    }
  };

  const unresolvedCount =
    readiness.category + readiness.section + readiness.group;
  const hasInventory = reviewedRows.length > 0;
  const isReady = hasInventory && unresolvedCount === 0;
  const convertedCount = preparedRows.filter((row) => row.isConverted).length;
  const duplicateSummary = useMemo(
    () => buildDuplicateMergeSummary(engine.rawRows || [], engine.mapping),
    [engine.rawRows, engine.mapping],
  );
  const acceptedDuplicateKeys = useMemo(
    () => new Set((engine?.config?.acceptedDuplicateKeys || []).map(clean)),
    [engine?.config?.acceptedDuplicateKeys],
  );
  const pendingDuplicateItems = useMemo(
    () => (duplicateSummary.items || []).filter((item) => !acceptedDuplicateKeys.has(clean(item.duplicateKey))),
    [duplicateSummary.items, acceptedDuplicateKeys],
  );
  const pendingDuplicateCount = useMemo(
    () => pendingDuplicateItems.reduce((sum, item) => sum + Number(item.merged || 0), 0),
    [pendingDuplicateItems],
  );

  const acceptDuplicateItems = async (items) => {
    if (duplicateDecisionSaving) return false;
    const next = new Set((engine?.config?.acceptedDuplicateKeys || []).map(clean));
    (items || []).forEach((item) => next.add(clean(item.duplicateKey)));
    const nextEngine = {
      ...engine,
      config: {
        ...(engine.config || {}),
        acceptedDuplicateKeys: Array.from(next).filter(Boolean),
      },
    };

    // Save the compact duplicate ledger first and wait for Firestore's exact
    // acknowledgement. The full 3,300-row engine document may be too large,
    // so closing the modal before this compact write completed caused the UI
    // to look successful while refresh restored the unresolved groups.
    setDuplicateDecisionSaving(true);
    sharedHydrationPausedRef.current = true;
    saveEngineState(nextEngine, activeStoreScope);
    setEngine(nextEngine);
    try {
      const saved = await saveDuplicateReviewDecisions(nextEngine, activeStoreScope);
      if (!saved) throw new Error("Duplicate review confirmation timed out.");
      setReviewNotice(`${items.length} duplicate product group${items.length === 1 ? "" : "s"} accepted and saved.`);
      // Best-effort full engine persistence remains useful for smaller imports,
      // but duplicate durability no longer depends on this large document.
      saveEngineStateDurable(nextEngine, activeStoreScope).catch(() => {});
      return true;
    } catch (error) {
      console.error("Failed to persist duplicate review decision", error);
      setReviewNotice("The duplicate decision could not be confirmed. Please try again before refreshing.");
      return false;
    } finally {
      setDuplicateDecisionSaving(false);
      sharedHydrationPausedRef.current = false;
      window.setTimeout(() => setReviewNotice(""), 30000);
    }
  };

  const ignoreDuplicateItem = (item) => {
    const ignored = new Set((engine?.config?.ignoredInventorySkus || []).map(clean));
    if (item?.sku) ignored.add(clean(item.sku));
    else if (item?.product) ignored.add(clean(item.product));
    const accepted = new Set((engine?.config?.acceptedDuplicateKeys || []).map(clean));
    accepted.add(clean(item.duplicateKey));
    commitInventoryDecision(
      {
        ...engine,
        config: {
          ...(engine.config || {}),
          ignoredInventorySkus: Array.from(ignored).filter(Boolean),
          acceptedDuplicateKeys: Array.from(accepted).filter(Boolean),
        },
      },
      `${item?.product || "Duplicate product"} excluded from Morpheus counts.`,
    );
  };

  const cycleOptions = useMemo(
    () =>
      cycles
        .filter((item) => item?.active !== false)
        .map((item) => ({
          value: normalizeCycleId(item.cycleId || item.id || item.name),
          label: item.name || formatCycleLabel(item.cycleId || item.id),
        })),
    [cycles],
  );

  const sectionOptions = useMemo(() => {
    const assignedOptions = sections
      .filter((item) => item?.active !== false)
      .map((item) => ({
        value: normalizeSectionId(item.sectionId || item.id || item.name),
        label: item.name || formatSectionLabel(item.sectionId || item.id),
        fullSite: false,
        sectionId: normalizeSectionId(item.sectionId || item.id || item.name),
        sectionName: item.name || formatSectionLabel(item.sectionId || item.id),
      }))
      .filter((item) => hasAllSectionAccess || allowedSectionIds.includes(item.sectionId));

    return hasAllSectionAccess
      ? [{
          value: "FULL_SITE",
          label: "Full Site Count",
          fullSite: true,
          sectionId: "",
          sectionName: "",
        }, ...assignedOptions]
      : assignedOptions;
  }, [sections, hasAllSectionAccess, allowedSectionIds]);

  const configuredGroups = useMemo(
    () => normalizeCountGroups(engine?.config?.categoryGroups),
    [engine],
  );
  const knownOutputs = useMemo(
    () =>
      new Set(
        [
          ...(engine?.rules || []).map((rule) => clean(rule?.output)),
          ...(engine?.config?.categoryOrder || []).map(clean),
          ...Object.keys(engine?.config?.categoryOwnership || {}).map(clean),
          ...normalizeCountGroups(engine?.config?.categoryGroups).flatMap(
            (group) => (group?.outputs || []).map(clean),
          ),
        ].filter(Boolean),
      ),
    [engine],
  );
  const knownOutputOptions = useMemo(
    () => {
      const values = [
        ...(engine?.rules || []).map((rule) => rule?.output),
        ...(engine?.config?.categoryOrder || []),
        ...Object.keys(engine?.config?.categoryOwnership || {}),
        ...normalizeCountGroups(engine?.config?.categoryGroups).flatMap(
          (group) => group?.outputs || [],
        ),
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean);
      return Array.from(
        new Map(values.map((value) => [clean(value), value])).values(),
      ).sort((a, b) => a.localeCompare(b));
    },
    [engine],
  );
  const bulkDraftIsValid = (draft) =>
    Boolean(
      clean(draft?.output) &&
      (draft?.outputMode === "existing"
        ? knownOutputs.has(clean(draft.output))
        : !knownOutputs.has(clean(draft.output))) &&
      (draft?.sectionMode === "new"
        ? clean(draft?.sectionName)
        : draft?.sectionId) &&
      clean(draft?.groupName),
    );
  const completedBulkDrafts = selectedPatterns.filter((item) =>
    bulkDraftIsValid(bulkRuleDrafts[patternKey(item)]),
  ).length;
  const newOutputCount = selectedPatterns.filter((item) => {
    const draft = bulkRuleDrafts[patternKey(item)] || {};
    const output = clean(draft.output);
    return output && draft.outputMode !== "existing" && !knownOutputs.has(output);
  }).length;
  const existingOutputCount = Math.max(0, completedBulkDrafts - newOutputCount);
  const newSectionCount = new Set(
    selectedPatterns
      .map((item) => {
        const draft = bulkRuleDrafts[patternKey(item)];
        return draft?.sectionMode === "new" ? clean(draft.sectionName) : "";
      })
      .filter(Boolean),
  ).size;
  const allBulkDraftsValid =
    selectedPatterns.length > 0 &&
    completedBulkDrafts === selectedPatterns.length;
  const reviewBulkChanges = useMemo(
    () =>
      selectedPatterns.map((item) => {
        const key = patternKey(item);
        const draft = bulkRuleDrafts[key] || {};
        const output = String(draft.output || "").trim();
        const sectionName =
          draft.sectionMode === "new"
            ? String(draft.sectionName || "").trim()
            : formatSectionLabel(draft.sectionId);
        const groupName = String(draft.groupName || "").trim();
        return {
          key,
          keyword: titleCase(item.keyword),
          products: item.rows.length,
          confidence: item.confidence,
          output,
          sectionName,
          sectionId:
            draft.sectionMode === "new"
              ? normalizeSectionId(sectionName)
              : draft.sectionId,
          sectionMode: draft.sectionMode,
          outputMode: draft.outputMode || (knownOutputs.has(clean(output)) ? "existing" : "new"),
          groupName,
          groupMode: draft.groupMode,
          outputIsNew:
            output &&
            draft.outputMode !== "existing" &&
            !knownOutputs.has(clean(output)),
          sectionIsNew: draft.sectionMode === "new",
          groupIsNew: draft.groupMode === "new",
        };
      }),
    [selectedPatterns, bulkRuleDrafts, knownOutputs],
  );

  const newGroupCount = new Set(
    reviewBulkChanges
      .filter((item) => item.groupIsNew)
      .map((item) => clean(item.groupName))
      .filter(Boolean),
  ).size;

  const inventoryLearningHistoryKey = `mc.inventoryLearningHistory.${clean(activeStoreScope || "ALL") || "all"}`;

  const readLearningTransactions = async () => {
    const indexed = await idbGetJson(inventoryLearningHistoryKey, null);
    if (Array.isArray(indexed)) return indexed;
    try {
      const legacy = JSON.parse(window.localStorage.getItem(inventoryLearningHistoryKey) || "[]");
      if (Array.isArray(legacy) && legacy.length) {
        await idbSetJson(inventoryLearningHistoryKey, legacy, { type: "inventory-learning-history" });
        try { window.localStorage.removeItem(inventoryLearningHistoryKey); } catch (_) {}
        return legacy;
      }
    } catch (_) {}
    return [];
  };

  const writeLearningTransaction = async (transaction) => {
    try {
      const existing = await readLearningTransactions();
      const next = [transaction, ...existing].slice(0, 100);
      await idbSetJson(inventoryLearningHistoryKey, next, { type: "inventory-learning-history" });
    } catch (error) {
      console.warn("Could not record inventory-learning transaction", error);
    }
  };

  const updateLearningTransaction = async (transactionId, patch) => {
    try {
      const existing = await readLearningTransactions();
      const next = existing.map((item) =>
        item?.transactionId === transactionId
          ? { ...item, ...patch, updatedAt: new Date().toISOString() }
          : item,
      );
      await idbSetJson(inventoryLearningHistoryKey, next, { type: "inventory-learning-history" });
    } catch (error) {
      console.warn("Could not update inventory-learning transaction", error);
    }
  };

  const commitBulkLearning = async () => {
    if (!allBulkDraftsValid || bulkLearnStage === "saving") return;
    sharedHydrationPausedRef.current = true;
    bulkLearnCancelRef.current = false;
    setBulkLearnError("");
    setBulkConfirmOpen(false);
    setBulkLearnStage("saving");
    const transactionId = `inventory-learn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setBulkTransactionId(transactionId);
    await writeLearningTransaction({
      transactionId,
      type: "inventory-learning",
      status: "started",
      startedAt: new Date().toISOString(),
      user: buildProfileDisplayName(currentProfile) || "User",
      scope: activeStoreScope || "ALL",
      productsAffected: selectedPatternProducts,
      rulesPlanned: selectedPatterns.length,
      readinessBefore: intelligenceScore,
      readinessAfter: projectedReadiness,
      patternsInitiallySelected: bulkInitiallySelectedKeys,
      patternsDeselected: bulkInitiallySelectedKeys.filter(
        (key) => !selectedPatternKeys.includes(key),
      ),
      patternsCommitted: selectedPatternKeys,
      changes: reviewBulkChanges,
    });
    const steps = [
      "Creating outputs",
      "Creating sections",
      "Creating Count Groups",
      "Creating rules",
      "Normalizing products",
      "Refreshing intelligence",
      "Recalculating readiness",
    ];
    setBulkLearnProgress(steps.map((label) => ({ label, status: "pending" })));
    const previousSections = activeSections();
    const previousAllEngine = loadEngineState("ALL");
    const previousScopedEngine = loadEngineState(activeStoreScope);
    try {
      const nextRules = [...(engine.rules || [])];
      const nextConfig = { ...(engine.config || {}) };
      nextConfig.categoryOrder = [...(nextConfig.categoryOrder || [])];
      nextConfig.categoryOwnership = {
        ...(nextConfig.categoryOwnership || {}),
      };
      let groups = normalizeCountGroups(nextConfig.categoryGroups);
      let nextSections = [...previousSections];

      const mark = (index, status = "done") => {
        setBulkLearnProgress((current) =>
          current.map((item, itemIndex) =>
            itemIndex === index ? { ...item, status } : item,
          ),
        );
      };
      const assertNotCancelled = () => {
        if (bulkLearnCancelRef.current) {
          throw new Error("Inventory learning was cancelled.");
        }
      };
      const pause = async () => {
        await new Promise((resolve) => window.setTimeout(resolve, 180));
        assertNotCancelled();
      };

      reviewBulkChanges.forEach((change) => {
        const output = clean(change.output);
        if (output && !nextConfig.categoryOrder.map(clean).includes(output))
          nextConfig.categoryOrder.push(output);
      });
      mark(0);
      await pause();

      reviewBulkChanges.forEach((change) => {
        if (!change.sectionIsNew) return;
        const sectionId = normalizeSectionId(change.sectionName);
        if (
          !nextSections.some(
            (section) =>
              normalizeSectionId(
                section.sectionId || section.id || section.name,
              ) === sectionId,
          )
        ) {
          nextSections.push({
            id: sectionId,
            sectionId,
            name: change.sectionName,
            active: true,
          });
        }
      });
      mark(1);
      await pause();

      reviewBulkChanges.forEach((change) => {
        const output = clean(change.output);
        const existing = groups.find(
          (group) => clean(group.name) === clean(change.groupName),
        );
        groups = existing
          ? groups.map((group) =>
              clean(group.name) === clean(change.groupName)
                ? {
                    ...group,
                    outputs: Array.from(
                      new Set([...(group.outputs || []), output]),
                    ),
                  }
                : group,
            )
          : [...groups, { name: change.groupName, outputs: [output] }];
      });
      nextConfig.categoryGroups = groups;
      mark(2);
      await pause();

      reviewBulkChanges.forEach((change) => {
        const keyword = clean(change.keyword);
        const output = clean(change.output);
        if (
          !nextRules.some(
            (rule) =>
              clean(rule.type) === "contains" &&
              clean(rule.value) === keyword &&
              clean(rule.output) === output,
          )
        ) {
          nextRules.push({ type: "contains", value: keyword, output });
        }
        nextConfig.categoryOwnership[output] = change.sectionId;
      });
      mark(3);
      await pause();
      mark(4);
      await pause();

      const nextEngine = { ...engine, rules: nextRules, config: nextConfig };

      // Inventory Configuration is the permanent source of truth. Persist the
      // learned category logic globally, then propagate it into every existing
      // site scope. Sections are stored in their own shared source.
      const persistedSections = saveSections(nextSections);

      // Build the exact global and selected-site payloads from the learning
      // plan. Do not reload immediately after a local save: shared hydration or
      // a full browser cache can return the previous engine and silently turn
      // the commit into a no-op.
      const currentAllEngine = loadEngineState("ALL") || buildEmptyState("ALL");
      const currentScopedEngine =
        loadEngineState(activeStoreScope) || buildEmptyState(activeStoreScope);
      const persistedAllEngine = recomputeEngineState(
        {
          ...currentAllEngine,
          rules: nextRules,
          config: {
            ...(currentAllEngine.config || {}),
            ...nextConfig,
            categoryOverride: false,
          },
        },
        { forceInventoryRemap: false },
      );
      const persistedScopedEngine = recomputeEngineState(
        {
          ...currentScopedEngine,
          rules: nextRules,
          config: {
            ...(currentScopedEngine.config || {}),
            ...nextConfig,
            storeName: currentScopedEngine.config?.storeName,
            categoryOverride: false,
          },
        },
        { forceInventoryRemap: true },
      );

      // Commit the engine and its separate shared Groups/Ownership overlays as
      // one durable operation. Previously the engine document was updated but
      // stale overlay documents were left behind; on refresh those stale
      // overlays replaced the newly learned section/group assignments.
      assertNotCancelled();
      // The full import engine may be larger than Firestore's document limit
      // because it includes raw product descriptions and prepared rows. The
      // learned configuration itself is compact, so commit that authoritative
      // category backup first and treat the full-engine mirror as best effort.
      const cloudWriteTasks = [saveCategoryBackupDurable(persistedAllEngine, "ALL")];
      if (normalizeStoreValue(activeStoreScope || "ALL") !== "ALL") {
        cloudWriteTasks.push(saveCategoryBackupDurable(persistedScopedEngine, activeStoreScope));
      }
      // Do not make an unchanged sections document a prerequisite for learning.
      // It previously caused valid category-rule commits to fail even when the
      // learning plan created zero sections.
      if (reviewBulkChanges.some((change) => change.sectionIsNew)) {
        cloudWriteTasks.push(
          mcSetCloudItemImmediate(MC_SECTIONS_KEY, JSON.stringify(persistedSections)),
        );
      }
      const cloudWrites = await Promise.all(cloudWriteTasks);
      assertNotCancelled();
      if (cloudWrites.some((saved) => saved !== true)) {
        throw new Error(
          "Inventory Configuration could not be confirmed in the shared source of truth.",
        );
      }

      // Keep the complete local engine immediately available. Mirror it to the
      // cloud without blocking learning success; oversized source rows can make
      // this optional mirror fail while the compact configuration is already
      // safely committed above.
      saveEngineState(persistedAllEngine, "ALL");
      saveEngineStateDurable(persistedAllEngine, "ALL").catch(() => {});
      if (normalizeStoreValue(activeStoreScope || "ALL") !== "ALL") {
        saveEngineState(persistedScopedEngine, activeStoreScope);
        saveEngineStateDurable(persistedScopedEngine, activeStoreScope).catch(() => {});
      }
      mark(5);
      await pause();

      // Read back from storage instead of trusting the in-memory plan. The
      // success state is blocked unless every Stage 1 entity is verifiably
      // present in the source of truth.
      const verifiedAllEngine = persistedAllEngine;
      const verifiedScopedEngine = persistedScopedEngine;
      const verifiedSections = persistedSections;
      if (!verifiedAllEngine || !verifiedScopedEngine) {
        throw new Error("Inventory Configuration could not be read back after saving.");
      }

      const verifiedRules = Array.isArray(verifiedAllEngine.rules)
        ? verifiedAllEngine.rules
        : [];
      const verifiedOrder = (verifiedAllEngine.config?.categoryOrder || []).map(clean);
      const verifiedGroups = normalizeCountGroups(
        verifiedAllEngine.config?.categoryGroups,
      );
      const verifiedOwnership = verifiedAllEngine.config?.categoryOwnership || {};

      const verificationErrors = [];
      reviewBulkChanges.forEach((change) => {
        const keyword = clean(change.keyword);
        const output = clean(change.output);
        const sectionId = normalizeSectionId(change.sectionId || change.sectionName);
        const groupName = clean(change.groupName);

        if (!verifiedOrder.includes(output)) {
          verificationErrors.push(`Output "${change.output}" was not created.`);
        }
        if (
          !verifiedRules.some(
            (rule) =>
              clean(rule.type) === "contains" &&
              clean(rule.value) === keyword &&
              clean(rule.output) === output,
          )
        ) {
          verificationErrors.push(`Rule "${change.keyword}" was not created.`);
        }
        const verifiedGroup = verifiedGroups.find(
          (group) => clean(group.name) === groupName,
        );
        if (
          !verifiedGroup ||
          !(verifiedGroup.outputs || []).map(clean).includes(output)
        ) {
          verificationErrors.push(`Count Group "${change.groupName}" was not assigned.`);
        }
        if (normalizeSectionId(verifiedOwnership[output] || "") !== sectionId) {
          verificationErrors.push(`Ownership for "${change.output}" was not saved.`);
        }
        if (
          change.sectionIsNew &&
          !verifiedSections.some(
            (section) =>
              normalizeSectionId(section.sectionId || section.id || section.name) ===
              sectionId,
          )
        ) {
          verificationErrors.push(`Section "${change.sectionName}" was not created.`);
        }
      });

      if (verificationErrors.length) {
        throw new Error(verificationErrors[0]);
      }

      // Stage 2: rebuild the active inventory snapshot with the verified
      // Inventory Configuration. Category settings live in the engine source of
      // truth, while raw inventory rows live in the active snapshot. Never
      // calculate readiness from a configuration-only engine because it can
      // legitimately contain zero raw rows.
      const activeSnapshot =
        peekActiveInventorySnapshot(activeStoreScope) ||
        (await loadActiveInventorySnapshot(activeStoreScope));
      const runtimeRows =
        activeSnapshot?.rawRows?.length
          ? activeSnapshot.rawRows
          : engine?.rawRows?.length
            ? engine.rawRows
            : verifiedScopedEngine.rawRows || [];
      const verifiedRuntimeEngine = {
        ...verifiedScopedEngine,
        ...(activeSnapshot || {}),
        rawRows: runtimeRows,
        mapping:
          activeSnapshot?.mapping ||
          engine?.mapping ||
          verifiedScopedEngine.mapping,
        config: verifiedScopedEngine.config,
        rules: verifiedScopedEngine.rules,
      };

      const confirmedPreparedRows = buildPreparedData(
        verifiedRuntimeEngine.rawRows || [],
        verifiedRuntimeEngine.mapping,
        verifiedRuntimeEngine.config,
        verifiedRuntimeEngine.rules,
      );
      const confirmedReviewedRows = confirmedPreparedRows.map((row) => ({
        ...row,
        readiness: rowReadiness(row, verifiedRuntimeEngine),
      }));
      const confirmedCounts = {
        ready: 0,
        category: 0,
        section: 0,
        group: 0,
        ignored: 0,
      };
      confirmedReviewedRows.forEach((row) => {
        const status = row.readiness?.status || "category";
        confirmedCounts[status] = (confirmedCounts[status] || 0) + 1;
      });
      const confirmedReadiness = confirmedReviewedRows.length
        ? Math.round(
            ((confirmedCounts.ready + confirmedCounts.ignored) /
              confirmedReviewedRows.length) *
              100,
          )
        : 0;
      const confirmedOperationalized = Math.max(
        0,
        confirmedCounts.ready +
          confirmedCounts.ignored -
          (readiness.ready + readiness.ignored),
      );
      const confirmedRemaining =
        confirmedCounts.category +
        confirmedCounts.section +
        confirmedCounts.group;

      if (!confirmedReviewedRows.length && reviewedRows.length) {
        throw new Error(
          "Inventory Configuration was saved, but the active inventory snapshot could not be rebuilt.",
        );
      }

      // The final progress check only completes after the real active snapshot
      // has been normalized and its readiness has been calculated.
      setSections(verifiedSections);
      setEngine(verifiedRuntimeEngine);
      mark(6);
      await pause();

      setUndoEngine(engine);
      setBulkCommitResult({
        rules: selectedPatterns.length,
        products: confirmedOperationalized,
        requestedProducts: selectedPatternProducts,
        remaining: confirmedRemaining,
        fromReadiness: intelligenceScore,
        toReadiness: confirmedReadiness,
      });
      setReviewNotice(
        `${selectedPatterns.length} rules learned. ${confirmedOperationalized} products became operational. ${confirmedRemaining} still need attention.`,
      );
      setSelectedPatternKeys([]);
      await updateLearningTransaction(transactionId, {
        status: "completed",
        completedAt: new Date().toISOString(),
        rulesCreated: selectedPatterns.length,
        productsRequested: selectedPatternProducts,
        productsNormalized: confirmedOperationalized,
        readinessProjected: projectedReadiness,
        readinessConfirmed: confirmedReadiness,
        unresolvedRemaining: confirmedRemaining,
        patternsDeselected: bulkInitiallySelectedKeys.filter(
          (key) => !selectedPatternKeys.includes(key),
        ),
        patternsCommitted: selectedPatternKeys,
      });
      sharedHydrationPausedRef.current = false;
      setBulkLearnStage("success");
    } catch (error) {
      sharedHydrationPausedRef.current = false;
      try {
        saveSections(previousSections);
        if (previousAllEngine) {
          saveEngineState(previousAllEngine, "ALL");
        }
        if (previousScopedEngine) {
          saveEngineState(previousScopedEngine, activeStoreScope);
        }
        setSections(activeSections());
        setEngine(loadEngineState(activeStoreScope) || previousScopedEngine || engine);
      } catch (_) {}
      await updateLearningTransaction(transactionId, {
        status: "failed",
        failedAt: new Date().toISOString(),
        error: error?.message || "Unknown error",
      });
      setBulkLearnError(
        error?.message || "Morpheus could not complete this inventory update.",
      );
      setBulkLearnStage("review");
    }
  };

  const selectedSectionOption = sectionOptions.find(
    (item) => item.value === selectedSection,
  );
  const applicableGroups = useMemo(() => {
    if (!selectedSectionOption) return [];
    if (selectedSectionOption.fullSite) return configuredGroups;
    const ownership = engine?.config?.categoryOwnership || {};
    return configuredGroups.filter((group) =>
      group.outputs.some(
        (output) =>
          normalizeSectionId(ownership?.[output] || "") ===
          selectedSectionOption.sectionId,
      ),
    );
  }, [configuredGroups, engine, selectedSectionOption]);

  useEffect(() => {
    setSelectedGroups([]);
    setSelectedProductIds([]);
    setProductPickerSearch("");
  }, [selectedSection]);
  useEffect(() => {
    setSelectedProductIds([]);
    setProductPickerSearch("");
  }, [selectedGroups]);
  useEffect(() => {
    if (selectedSection && !sectionOptions.some((item) => item.value === selectedSection)) {
      setSelectedSection("");
    }
  }, [selectedSection, sectionOptions]);

  const productScopeRows = useMemo(() => {
    if (!selectedSectionOption) return [];
    const ownership = engine?.config?.categoryOwnership || {};
    let rows = reviewedRows.filter((row) => row.readiness?.status === "ready");
    if (!selectedSectionOption.fullSite) {
      rows = rows.filter(
        (row) =>
          normalizeSectionId(
            ownership?.[clean(row.category)] || ownership?.[row.category] || "",
          ) === selectedSectionOption.sectionId,
      );
    }
    if (selectedGroups.length) {
      const outputs = new Set(
        configuredGroups
          .filter((group) => selectedGroups.includes(group.name))
          .flatMap((group) => group.outputs),
      );
      rows = rows.filter((row) => outputs.has(clean(row.category)));
    }
    return rows;
  }, [
    reviewedRows,
    selectedSectionOption,
    selectedGroups,
    configuredGroups,
    engine,
  ]);

  const scopedRows = useMemo(() => {
    if (!selectedProductIds.length) return productScopeRows;
    // ALL-site CSVs often contain one row per site for the same SKU. A selected
    // product is one logical product, not every row sharing that SKU/row ID.
    return selectedCountProducts(productScopeRows, selectedProductIds);
  }, [productScopeRows, selectedProductIds]);

  useEffect(() => {
    if (!selectedProductIds.length) return;
    const validKeys = new Set(productScopeRows.map(countProductSelectionKey));
    setSelectedProductIds((current) => current.filter((key) => validKeys.has(key)));
  }, [productScopeRows]);

  const estimatedMinutes = Math.max(1, Math.ceil(scopedRows.length * 0.42));
  const canPrepareScopedSession =
    isReady && selectedCycle && selectedSection && scopedRows.length > 0;

  const filteredRows = useMemo(() => {
    const needle = clean(search);
    return reviewedRows.filter((row) => {
      const statusMatch =
        activeFilter === "all" || row.readiness?.status === activeFilter;
      if (!statusMatch) return false;
      if (!needle) return true;
      return [
        row.product,
        row.sku,
        row.category,
        row.sourceCategory,
        row.readiness?.reason,
      ].some((value) => clean(value).includes(needle));
    });
  }, [reviewedRows, search, activeFilter]);

  const ignoredRows = useMemo(
    () => reviewedRows.filter((row) => row.readiness?.status === "ignored"),
    [reviewedRows],
  );

  const createSession = async (targetStoreScope = activeStoreScope) => {
    // The caller may already have raised the preparation lock so the spinner can
    // paint before any synchronous session-building work begins. Do not treat
    // that lock as a reason to abort the actual preparation.
    if (!sessionPreparing) {
      flushSync(() => {
        setSessionPreparing(true);
        setReviewNotice("");
      });
    } else {
      setReviewNotice("");
    }
    // Yield a full browser paint before parsing/building hundreds of rows.
    await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
    try {
    const cycle = cycleOptions.find((item) => item.value === selectedCycle);
    const section = sectionOptions.find(
      (item) => item.value === selectedSection,
    );
    if (!cycle || !section) {
      setSessionPreparing(false);
      return;
    }
    const sessionStoreKey = resolveInventoryHeaderForScope(
      targetStoreScope,
      engine.parsedHeaders || [],
      engine,
    );
    const sessionMapping = {
      ...(engine.mapping || {}),
      systemSoh: sessionStoreKey,
    };
    // Row-site exports share the same SOH header between stores. Project the
    // source onto the requested operational site BEFORE creating a count; the
    // global source must not leak another site's products into Bellville.
    const rawRows = Array.isArray(engine.rawRows) ? engine.rawRows : [];
    const profile = detectUniversalImportProfile(
      engine.parsedHeaders?.length ? engine.parsedHeaders : Object.keys(rawRows[0] || {}),
      rawRows,
      targetStoreScope,
    );
    const rowSiteHeader = profile.layout === "row_site" ? profile.rowSiteHeader : "";
    let sessionRawRows = rawRows;
    if (rowSiteHeader) {
      const site = resolveSite(targetStoreScope);
      const siteKeys = new Set([
        targetStoreScope, site?.name, site?.siteId, site?.storeKey,
      ].filter(Boolean).map((value) => normalizeStoreValue(value).toLowerCase()));
      sessionRawRows = rawRows.filter((row) =>
        siteKeys.has(normalizeStoreValue(row?.[rowSiteHeader]).toLowerCase()),
      );
      if (!sessionRawRows.length) {
        throw new Error(`No inventory rows were found for ${formatStoreLabel(targetStoreScope)}. Check the site in the CSV before preparing a count.`);
      }
    }
    const allPreparedRows = buildPreparedData(
      sessionRawRows,
      sessionMapping,
      engine.config,
      engine.rules,
    );
    const selectedKeys = new Set(selectedProductIds);
    const scopedPreparedRows = selectedKeys.size
      ? uniqueCountProducts(allPreparedRows.filter((row) =>
          selectedKeys.has(countProductSelectionKey(row)),
        ))
      : allPreparedRows;
    if (selectedKeys.size && scopedPreparedRows.length !== selectedKeys.size) {
      throw new Error(`Only ${scopedPreparedRows.length} of ${selectedKeys.size} selected products exist at ${formatStoreLabel(targetStoreScope)}. Select products for that site before preparing the count.`);
    }
    const engineForSession = {
      ...engine,
      rawRows: sessionRawRows,
      mapping: sessionMapping,
      preparedRows: scopedPreparedRows,
      lastGeneratedAt: new Date().toISOString(),
    };
    const sourceSite = resolveSite(targetStoreScope);
    if (!sourceSite?.siteId) throw new Error('Select a registered Morpheus site before preparing this count.');
    const payload = buildSessionPayloadFromEngine(engineForSession, {
      siteId: sourceSite.siteId,
      storeKey: sessionStoreKey,
      // Always persist the selected MatrixOS site as the session identity.
      // storeKey remains the source inventory column (for example "SOH"), but
      // it must never become the visible site name or the conflict key.
      storeName: formatStoreLabel(
        targetStoreScope || engineForSession?.config?.storeName,
      ),
      siteName: formatStoreLabel(
        targetStoreScope || engineForSession?.config?.storeName,
      ),
      startedAt: new Date().toISOString(),
      startedBy: buildProfileDisplayName(currentProfile) || "User",
      lastEditedBy: buildProfileDisplayName(currentProfile) || "User",
      cycleId: cycle.value,
      cycleName: cycle.label,
      cycleKey: cycle.value,
      cycle: { cycleId: cycle.value, id: cycle.value, name: cycle.label },
      sectionId: section.sectionId,
      sectionName: section.sectionName,
      fullSite: section.fullSite === true,
      categoryScopeType: selectedGroups.length ? "selected" : "all",
      categoryNames: selectedGroups,
      countGroupNames: selectedGroups,
      categoryIds: selectedGroups.map((name) =>
        clean(name).replace(/[^a-z0-9]+/g, "-"),
      ),
      categoryScopeLabel: selectedGroups.length
        ? selectedGroups.join(" • ")
        : "All Count Groups",
      categoryScopeSource: "count-groups",
    });
    payload.billingStartVersion = 1;
    payload.billingOriginalStartedAt = payload.startedAt;
    // Keep the session's product metadata aligned with the actual site-scoped
    // lines saved to Firestore. UI selection keys are not storage row IDs.
    payload.productScopeType = selectedProductIds.length ? "selected" : "all";
    payload.productIds = selectedProductIds.length
      ? payload.preparedRows.map((row) => row.id)
      : [];
    payload.productNames = selectedProductIds.length
      ? payload.preparedRows.map((row) => row.product || row.sku || row.id)
      : [];
    payload.productScopeLabel = selectedProductIds.length
      ? `${payload.preparedRows.length} selected product${payload.preparedRows.length === 1 ? "" : "s"}`
      : "All products";
    if (selectedProductIds.length && payload.preparedRows.length !== selectedProductIds.length) {
      throw new Error("Product selection did not match the count lines. The session was not saved.");
    }

    // Commit the complete session locally first so the live count can open as
    // soon as the CPU work is finished. The non-durable writers persist the
    // same data to Firestore in the background and dispatch the session events
    // immediately, so Sessions can see the count without waiting on network
    // round-trips.
    savePreparedRowsToSessionSeed(payload, sessionStoreKey);
    const sessionRecord = {
      sessionId: payload.sessionId,
      siteId: payload.siteId,
      operationalDate: payload.operationalDate,
      tenantProofVersion: 2,
      storeName: payload.storeName,
      storeKey: payload.storeKey,
      sectionId: payload.sectionId || "",
      sectionName: payload.sectionName || "",
      fullSite: payload.fullSite === true,
      cycleId: payload.cycleId || cycle.value,
      cycleName: payload.cycleName || cycle.label,
      cycleKey: payload.cycleKey || cycle.value,
      cycle: payload.cycle || { cycleId: cycle.value, id: cycle.value, name: cycle.label },
      categoryScopeType: payload.categoryScopeType,
      categoryNames: payload.categoryNames,
      countGroupNames: payload.countGroupNames,
      categoryIds: payload.categoryIds,
      categoryScopeLabel: payload.categoryScopeLabel,
      categoryScopeSource: payload.categoryScopeSource,
      productScopeType: payload.productScopeType || "all",
      productIds: payload.productIds || [],
      productNames: payload.productNames || [],
      productScopeLabel: payload.productScopeLabel || "All products",
      fileName: payload.fileName,
      startedAt: payload.startedAt,
      billingStartVersion: payload.billingStartVersion,
      billingOriginalStartedAt: payload.billingOriginalStartedAt,
      updatedAt: payload.startedAt,
      startedBy: payload.startedBy,
      lastEditedBy: payload.startedBy,
      totalLines: payload.preparedRows?.length || 0,
      completedLines: 0,
      flaggedCount: 0,
      status: "Active",
      costExposure: 0,
    };

    // The live-session index and detail must be durable before navigation.
    // Background-only writes allowed the detail page to open from local storage
    // while Firestore still had an empty mc-session-list; the next listener/refresh
    // then legitimately replaced Sessions with zero rows. Keep the local seed for
    // instant rendering, but commit both authoritative records before leaving Review.
    await Promise.all([
      saveSessionDetailFromPayloadDurable(payload),
      upsertSessionRecordDurable(sessionRecord),
    ]);
    recordAcceptedMcCountStart(getMcActiveOrgId(), payload.sessionId);
    navigate(`/morpheus-count/session/${payload.sessionId}`);
    } catch (error) {
      console.error("MC count preparation failed", error);
      setSessionPreparing(false);
      setReviewNotice(error?.message || "The count could not be prepared. Please try again.");
    }
  };

  const prepareNewSession = (targetStoreScope = activeStoreScope) => {
    if (unresolvedCount > 0) return;
    const normalizedTarget = normalizeStoreValue(targetStoreScope || "ALL");
    if (!normalizedTarget || normalizedTarget === "ALL") {
      const firstSite = prepareSiteOptions[0] || "";
      setPrepareSiteScope(firstSite);
      setPrepareSitePrompt(true);
      setSessionPreparing(false);
      return;
    }
    const sessionStoreKey = resolveInventoryHeaderForScope(
      normalizedTarget,
      engine.parsedHeaders || [],
      engine,
    );
    const sessionEngine = {
      ...engine,
      config: { ...(engine.config || {}), storeName: normalizedTarget },
      mapping: { ...(engine.mapping || {}), systemSoh: sessionStoreKey },
    };
    const existing = activeLiveSessionRecordsForEngine(
      sessionEngine,
      selectedSectionOption,
      { cycleId: selectedCycle },
    );
    if (existing.length) {
      setPendingPrepareScope(normalizedTarget);
      setOverwriteMatches(existing);
      setOverwritePrompt(true);
      setSessionPreparing(false);
      return;
    }
    setPendingPrepareScope("");
    createSession(normalizedTarget);
  };

  const confirmOverwrite = async () => {
    const targetScope = normalizeStoreValue(pendingPrepareScope || activeStoreScope || "ALL");
    const sessionStoreKey = resolveInventoryHeaderForScope(
      targetScope,
      engine.parsedHeaders || [],
      engine,
    );
    const sessionEngine = {
      ...engine,
      config: { ...(engine.config || {}), storeName: targetScope },
      mapping: { ...(engine.mapping || {}), systemSoh: sessionStoreKey },
    };
    const matches = activeLiveSessionRecordsForEngine(
      sessionEngine,
      selectedSectionOption,
      { cycleId: selectedCycle },
    );
    setOverwritePrompt(false);
    setOverwriteMatches([]);
    // archiveSessionRecord marks each matching session locally before its first
    // network await. Start those writes, but do not make the operator wait for
    // Firestore before opening the replacement count.
    matches.forEach((record) => {
      archiveSessionRecord(
        record.sessionId,
        "Overwritten by new session",
      ).catch((err) => {
        console.warn("MC prepared-data overwrite archive failed", err);
      });
    });
    setPendingPrepareScope("");
    createSession(targetScope);
  };

  const resumeExistingLiveSession = () => {
    const target = overwriteMatches?.[0]?.sessionId;
    setOverwritePrompt(false);
    setOverwriteMatches([]);
    if (target) navigate(`/morpheus-count/session/${target}`);
  };

  useEffect(() => {
    const refreshSites = () => deferPreparedEvent(
      "soh-sites",
      () => setSohSiteVersion((value) => value + 1),
    );
    window.addEventListener(MC_SITE_EVENT, refreshSites);
    window.addEventListener("storage", refreshSites);
    window.addEventListener(MC_SYNC_READY_EVENT, refreshSites);
    return () => {
      window.removeEventListener(MC_SITE_EVENT, refreshSites);
      window.removeEventListener("storage", refreshSites);
      window.removeEventListener(MC_SYNC_READY_EVENT, refreshSites);
    };
  }, []);

  useEffect(() => () => {
    preparedEventTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    preparedEventTimersRef.current.clear();
  }, []);

  const sohSourceProfile = useMemo(() => {
    if (activeStoreScope !== "ALL") return null;
    return detectUniversalImportProfile(
      engine?.parsedHeaders || [],
      engine?.rawRows || [],
      "",
    );
  }, [activeStoreScope, engine?.parsedHeaders, engine?.rawRows]);

  const sohSites = useMemo(() => {
    if (activeStoreScope !== "ALL") {
      return [{
        scope: activeStoreScope,
        label: formatStoreLabel(activeStoreScope),
      }];
    }

    // All Sites SOH must not depend on the asynchronously hydrated Sites registry
    // or on N separate site snapshot reads. The active ALL Review snapshot is
    // already the source of truth. Derive its operational sites directly from
    // the inventory columns (column-site CSV) or the row-site field.
    const rawRows = Array.isArray(engine?.rawRows) ? engine.rawRows : [];
    const profile = sohSourceProfile || {};
    const discovered = [];

    if (profile.layout === "column_site" && Array.isArray(profile.inventoryHeaders)) {
      profile.inventoryHeaders.forEach((header) => {
        const scope = normalizeStoreValue(header || "");
        if (!scope || scope === "ALL") return;
        discovered.push({
          scope,
          label: formatStoreLabel(header),
          inventoryHeader: header,
          sourceValue: "",
        });
      });
    } else if (profile.layout === "row_site" && profile.rowSiteHeader) {
      const seenValues = new Set();
      rawRows.forEach((row) => {
        const rawValue = String(row?.[profile.rowSiteHeader] ?? "").trim();
        if (!rawValue) return;
        const compare = clean(rawValue);
        if (!compare || seenValues.has(compare)) return;
        seenValues.add(compare);
        const scope = normalizeStoreValue(rawValue);
        if (!scope || scope === "ALL") return;
        discovered.push({
          scope,
          label: formatStoreLabel(rawValue),
          inventoryHeader: profile?.fields?.systemSoh?.header || engine?.mapping?.systemSoh || "",
          sourceValue: rawValue,
        });
      });
    }

    // Only fall back to the org registry when the active ALL snapshot genuinely
    // cannot describe its own site layout (for example a single-site source).
    if (!discovered.length) {
      loadSites()
        .filter((site) => site?.active !== false)
        .forEach((site) => {
          const scope = normalizeStoreValue(site?.siteId || site?.storeKey || site?.name || "");
          if (!scope || scope === "ALL") return;
          discovered.push({
            scope,
            label: site?.name || formatStoreLabel(scope),
            inventoryHeader: resolveInventoryHeaderForScope(scope, engine?.parsedHeaders || [], engine),
            sourceValue: "",
          });
        });
    }

    return discovered
      .filter((site, index, all) => all.findIndex((candidate) => candidate.scope === site.scope) === index)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [activeStoreScope, adminStoreView, sohSiteVersion, sohSourceProfile, engine?.rawRows, engine?.parsedHeaders, engine?.mapping?.systemSoh]);

  const sohSiteScopeKey = useMemo(
    () => sohSites.map((site) => `${site.scope}:${site.label}:${site.inventoryHeader || ""}:${site.sourceValue || ""}`).join("|"),
    [sohSites],
  );

  useEffect(() => {
    if (reviewSubTab !== "stock-on-hand" || activeStoreScope === "ALL") return;
    const site = sohSites[0] || {
      scope: activeStoreScope,
      label: formatStoreLabel(activeStoreScope),
    };
    setSohLoading(false);
    setSohSiteRows({
      [site.scope]: {
        site,
        rows: reviewedRows,
        syncedAt: engine?.uploadedAt || engine?.updatedAt || engine?.syncedAt || engine?.lastGeneratedAt || "",
        available: reviewedRows.length > 0,
        config: engine?.config || {},
      },
    });
  }, [reviewSubTab, activeStoreScope, sohSiteScopeKey, reviewedRows, engine?.uploadedAt, engine?.updatedAt, engine?.syncedAt, engine?.lastGeneratedAt, engine?.config]);

  useEffect(() => {
    if (reviewSubTab !== "stock-on-hand" || activeStoreScope !== "ALL") return;

    const rawRows = Array.isArray(engine?.rawRows) ? engine.rawRows : [];
    if (snapshotLoading && !rawRows.length) {
      setSohLoading(true);
      return;
    }

    setSohLoading(false);
    if (!rawRows.length || !sohSites.length) {
      setSohSiteRows({});
      return;
    }

    const globalStored = loadLatestImportEngineState("ALL") || loadEngineState("ALL") || buildEmptyState("ALL");
    const profile = sohSourceProfile || detectUniversalImportProfile(engine?.parsedHeaders || [], rawRows, "");
    const next = {};

    sohSites.forEach((site) => {
      let siteRawRows = rawRows;
      let systemSohHeader = site.inventoryHeader || "";

      if (profile?.layout === "row_site" && profile?.rowSiteHeader && site.sourceValue) {
        const target = clean(site.sourceValue);
        siteRawRows = rawRows.filter((row) => clean(row?.[profile.rowSiteHeader]) === target);
        systemSohHeader = systemSohHeader || profile?.fields?.systemSoh?.header || engine?.mapping?.systemSoh || "";
      } else if (profile?.layout === "column_site") {
        systemSohHeader = systemSohHeader || resolveInventoryHeaderForScope(site.scope, engine?.parsedHeaders || [], engine);
      }

      if (!siteRawRows.length || !systemSohHeader) {
        next[site.scope] = { site, rows: [], syncedAt: "", available: false, config: engine?.config || {} };
        return;
      }

      const scopedStored = loadLatestImportEngineState(site.scope) || loadEngineState(site.scope) || buildEmptyState(site.scope);
      const runtimeEngine = mergeGlobalInventoryConfiguration(globalStored, {
        ...scopedStored,
        ...engine,
        rawRows: siteRawRows,
        mapping: {
          ...(engine?.mapping || {}),
          ...(scopedStored?.mapping || {}),
          systemSoh: systemSohHeader,
          ...(profile?.layout === "row_site" && profile?.rowSiteHeader ? { site: profile.rowSiteHeader } : {}),
        },
        config: {
          ...(engine?.config || {}),
          ...(scopedStored?.config || {}),
          storeName: site.scope,
        },
        rules: Array.isArray(scopedStored?.rules) && scopedStored.rules.length
          ? scopedStored.rules
          : (engine?.rules || globalStored?.rules || []),
      });

      const rows = buildPreparedData(
        siteRawRows,
        runtimeEngine.mapping || {},
        runtimeEngine.config || {},
        runtimeEngine.rules || [],
      );
      next[site.scope] = {
        site,
        rows,
        syncedAt: engine?.uploadedAt || engine?.updatedAt || engine?.syncedAt || engine?.lastGeneratedAt || "",
        available: rows.length > 0,
        config: runtimeEngine?.config || {},
      };
    });

    setSohSiteRows(next);
  }, [
    reviewSubTab,
    activeStoreScope,
    sohSiteScopeKey,
    sohSourceProfile,
    snapshotLoading,
    engine?.rawRows,
    engine?.parsedHeaders,
    engine?.mapping,
    engine?.config,
    engine?.rules,
    engine?.uploadedAt,
    engine?.updatedAt,
    engine?.syncedAt,
    engine?.lastGeneratedAt,
  ]);

  const sohInventory = useMemo(() => {
    const map = new Map();
    sohSites.forEach((site) => {
      const siteData = sohSiteRows?.[site.scope];
      const groups = normalizeCountGroups(siteData?.config?.categoryGroups || engine?.config?.categoryGroups || []);
      (siteData?.rows || []).forEach((row) => {
        const key = clean(row.sku) || clean(row.product);
        if (!key) return;
        const category = row.category === "__unmapped__" ? "Unmapped" : (row.category || "Unmapped");
        const matchedGroup = groups.find((group) => group.outputs.includes(clean(category)));
        const groupName = matchedGroup?.name || "Ungrouped";
        const existing = map.get(key) || {
          key,
          product: row.product || "Unnamed product",
          sku: row.sku || "",
          group: groupName,
          sourceCost: row.sourceCost ?? row.cost ?? 0,
          priceBasis: row.priceBasis || "count",
          priceUnitSize: row.priceUnitSize || 1,
          baseUnit: row.baseUnit || "Unit",
          sites: {},
        };
        if ((!existing.group || existing.group === "Ungrouped") && groupName !== "Ungrouped") existing.group = groupName;
        if ((existing.sourceCost === null || existing.sourceCost === undefined || existing.sourceCost === "") && row.sourceCost !== undefined) {
          existing.sourceCost = row.sourceCost;
          existing.priceBasis = row.priceBasis || existing.priceBasis || "count";
          existing.priceUnitSize = row.priceUnitSize || existing.priceUnitSize || 1;
          existing.baseUnit = row.baseUnit || existing.baseUnit || "Unit";
        }
        const value = Number(row.realSoh ?? row.systemSoh ?? 0);
        existing.sites[site.scope] = (existing.sites[site.scope] || 0) + (Number.isFinite(value) ? value : 0);
        map.set(key, existing);
      });
    });
    return Array.from(map.values()).sort((a, b) => a.product.localeCompare(b.product));
  }, [sohSiteRows, sohSites, engine?.config?.categoryGroups]);

  const sohGroups = useMemo(() => Array.from(new Set(
    sohInventory.map((row) => row.group).filter(Boolean),
  )).sort((a, b) => a.localeCompare(b)), [sohInventory]);

  const sohProductOptions = useMemo(() => sohInventory
    .filter((row) => !sohGroupsSelected.length || sohGroupsSelected.includes(row.group))
    .sort((a, b) => a.product.localeCompare(b.product)), [sohInventory, sohGroupsSelected]);

  useEffect(() => {
    setSohProductsSelected((current) => current.filter((key) => sohProductOptions.some((row) => row.key === key)));
  }, [sohProductOptions]);

  const baseFilteredSohInventory = useMemo(() => {
    const needle = clean(sohSearch);
    return sohInventory.filter((row) => {
      if (sohGroupsSelected.length && !sohGroupsSelected.includes(row.group)) return false;
      if (sohProductsSelected.length && !sohProductsSelected.includes(row.key)) return false;
      if (!needle) return true;
      return [row.product, row.sku, row.group].some((value) => clean(value).includes(needle));
    });
  }, [sohInventory, sohGroupsSelected, sohProductsSelected, sohSearch]);

  const classifySohRow = (row) => {
    const siteValues = sohSites
      .filter((site) => sohSiteRows?.[site.scope]?.available !== false)
      .map((site) => Number(row.sites?.[site.scope] || 0));
    const outOfStock = siteValues.length > 0 && siteValues.every((value) => value <= 0);
    const lowStock = !outOfStock && siteValues.some((value) => value > 0 && value <= 5);
    const inStock = siteValues.some((value) => value > 0);
    return { siteValues, outOfStock, lowStock, inStock };
  };

  const filteredSohInventory = useMemo(() => {
    const filtered = baseFilteredSohInventory.filter((row) => {
      if (sohMetricFilter === "out") return classifySohRow(row).outOfStock;
      if (sohMetricFilter === "low") return classifySohRow(row).lowStock;
      if (sohMetricFilter === "units") return classifySohRow(row).inStock;
      return true;
    });

    const direction = sohSortDirection === "desc" ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const primaryA = sohSortBy === "group" ? (a.group || "") : (a.product || "");
      const primaryB = sohSortBy === "group" ? (b.group || "") : (b.product || "");
      const primary = primaryA.localeCompare(primaryB, undefined, { sensitivity: "base", numeric: true });
      if (primary) return primary * direction;
      return (a.product || "").localeCompare(b.product || "", undefined, { sensitivity: "base", numeric: true }) * direction;
    });
  }, [baseFilteredSohInventory, sohMetricFilter, sohSites, sohSiteRows, sohSortBy, sohSortDirection]);

  const sohSummary = useMemo(() => {
    let units = 0;
    let zeroRows = 0;
    let lowRows = 0;
    baseFilteredSohInventory.forEach((row) => {
      const classification = classifySohRow(row);
      units += classification.siteValues.reduce((sum, value) => sum + value, 0);
      if (classification.outOfStock) zeroRows += 1;
      else if (classification.lowStock) lowRows += 1;
    });
    return { products: baseFilteredSohInventory.length, units, zeroRows, lowRows };
  }, [baseFilteredSohInventory, sohSites, sohSiteRows]);

  const sohPriceLabel = (row = {}) => {
    const sourceCost = Number(row?.sourceCost ?? 0);
    if (!Number.isFinite(sourceCost)) return "Imported R0 per 1 Unit";
    const amount = Number.isInteger(sourceCost) ? String(sourceCost) : sourceCost.toFixed(2);
    const baseUnit = row?.baseUnit || "Unit";
    if (row?.priceBasis === "base") return `Imported R${amount} per ${baseUnit}`;
    return `Imported R${amount} per ${formatNumber(row?.priceUnitSize || 1)} ${baseUnit}`;
  };

  const exportSohPdf = () => {
    if (!filteredSohInventory.length) return;

    const light = isMorpheusLightTheme();
    const allSites = activeStoreScope === "ALL";
    const doc = new jsPDF({ orientation: allSites ? "landscape" : "portrait", unit: "mm", format: allSites ? "a3" : "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const bg = light ? [248, 251, 253] : [4, 18, 35];
    const panel = light ? [255, 255, 255] : [6, 26, 48];
    const ink = light ? [16, 46, 66] : [239, 248, 255];
    const muted = light ? [82, 111, 130] : [127, 165, 193];
    const accent = [47, 197, 236];
    const border = light ? [205, 221, 230] : [31, 68, 94];
    const scopeLabel = allSites ? "All Sites" : formatStoreLabel(activeStoreScope);

    const groupFilterLabel = !sohGroupsSelected.length
      ? "All Count Groups"
      : sohGroupsSelected.length === 1
        ? sohGroupsSelected[0]
        : `${sohGroupsSelected.length} selected`;
    const productFilterLabel = !sohProductsSelected.length
      ? "All products"
      : sohProductsSelected.length === 1
        ? (sohInventory.find((row) => row.key === sohProductsSelected[0])?.product || sohProductsSelected[0])
        : `${sohProductsSelected.length} selected`;

    const filters = [
      `Scope: ${scopeLabel}`,
      `Count Group: ${groupFilterLabel}`,
      `Product: ${productFilterLabel}`,
      `Stock filter: ${sohMetricFilter === "all" ? "All products" : sohMetricFilter === "units" ? "In stock" : sohMetricFilter === "out" ? "Out of stock" : "Low stock"}`,
      `Sort: ${sohSortBy === "group" ? "Count Group" : "Product"} ${sohSortDirection === "desc" ? "Z–A" : "A–Z"}`,
    ];

    const drawFirstPageHeader = () => {
      doc.setTextColor(...accent);
      doc.setFontSize(8);
      doc.text("MATRIXOS · MORPHEUS · STOCK ON HAND", 12, 12);
      doc.setTextColor(...ink);
      doc.setFontSize(20);
      doc.text("Stock on Hand", 12, 22);
      doc.setFontSize(9);
      doc.setTextColor(...muted);
      doc.text(filters.join("  •  "), 12, 29, { maxWidth: pageWidth - 24 });

      const metrics = [
        ["Products", String(sohSummary.products)],
        ["Total units", formatNumber(sohSummary.units)],
        ["Out of stock", String(sohSummary.zeroRows)],
        ["Low stock", String(sohSummary.lowRows)],
      ];
      const metricY = 35;
      const gap = 3;
      const cardW = (pageWidth - 24 - gap * 3) / 4;
      metrics.forEach(([label, value], index) => {
        const x = 12 + index * (cardW + gap);
        doc.setFillColor(...panel);
        doc.setDrawColor(...border);
        doc.roundedRect(x, metricY, cardW, 15, 2, 2, "FD");
        doc.setTextColor(...muted);
        doc.setFontSize(7);
        doc.text(label.toUpperCase(), x + 3, metricY + 5);
        doc.setTextColor(...ink);
        doc.setFontSize(12);
        doc.text(value, x + 3, metricY + 11);
      });
    };

    const head = [["Product", "SKU", "Count Group", ...sohSites.map((site) => site.label), ...(allSites ? ["Total"] : [])]];
    const body = filteredSohInventory.map((row) => {
      const values = sohSites.map((site) => sohSiteRows?.[site.scope]?.available === false ? "—" : formatNumber(row.sites?.[site.scope] || 0));
      const total = sohSites.reduce((sum, site) => sum + Number(row.sites?.[site.scope] || 0), 0);
      return [row.product, row.sku || "—", row.group || "—", ...values, ...(allSites ? [formatNumber(total)] : [])];
    });

    autoTable(doc, {
      head,
      body,
      startY: 54,
      theme: "grid",
      margin: { left: 12, right: 12, top: 12, bottom: 13 },
      styles: {
        fontSize: allSites ? 6.2 : 7.6,
        cellPadding: allSites ? 1.4 : 1.8,
        fillColor: panel,
        textColor: ink,
        lineColor: border,
        lineWidth: 0.15,
        overflow: "linebreak",
        valign: "middle",
      },
      headStyles: {
        fillColor: light ? [235, 244, 248] : [6, 26, 48],
        textColor: light ? [36, 69, 91] : [127, 199, 229],
        fontStyle: "bold",
        lineColor: border,
      },
      columnStyles: {
        0: { cellWidth: allSites ? 44 : 62, fontStyle: "bold", valign: "top", minCellHeight: allSites ? 10 : 11 },
        1: { cellWidth: allSites ? 19 : 24 },
        2: { cellWidth: allSites ? 27 : 34 },
      },
      didParseCell: (data) => {
        if (data.section !== "body" || data.column.index !== 0) return;
        const row = filteredSohInventory[data.row.index];
        if (!row) return;
        const padX = allSites ? 1.4 : 1.8;
        const productFontSize = allSites ? 6.2 : 7.6;
        const priceFontSize = allSites ? 5.2 : 6.2;
        const usableWidth = Math.max(10, data.cell.styles.cellWidth - padX * 2);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(productFontSize);
        const productLines = doc.splitTextToSize(String(row.product || ""), usableWidth);
        const productLineHeight = productFontSize * 0.3528 * 1.18;
        const priceLineHeight = priceFontSize * 0.3528 * 1.18;
        const productPriceGap = allSites ? 1.5 : 1.9;
        const neededHeight =
          (allSites ? 1.4 : 1.8) +
          productLines.length * productLineHeight +
          productPriceGap +
          priceLineHeight +
          (allSites ? 1.4 : 1.8);
        data.cell.styles.minCellHeight = Math.max(data.cell.styles.minCellHeight || 0, neededHeight);
      },
      didDrawCell: (data) => {
        if (data.section !== "body" || data.column.index !== 0) return;
        const row = filteredSohInventory[data.row.index];
        if (!row) return;
        const padX = allSites ? 1.4 : 1.8;
        const padY = allSites ? 1.4 : 1.8;
        const productFontSize = allSites ? 6.2 : 7.6;
        const priceFontSize = allSites ? 5.2 : 6.2;
        const usableWidth = Math.max(10, data.cell.width - padX * 2);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(productFontSize);
        const productLines = doc.splitTextToSize(String(row.product || ""), usableWidth);
        // Anchor the muted price line to the bottom of the cell rather than to
        // an estimated product baseline. This keeps a guaranteed visual gap
        // even when the product name wraps to two or more lines.
        const bottomPad = allSites ? 1.4 : 1.8;
        const priceFontHeight = priceFontSize * 0.3528;
        const priceY = data.cell.y + data.cell.height - bottomPad - (priceFontHeight * 0.15);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(priceFontSize);
        doc.setTextColor(...muted);
        doc.text(sohPriceLabel(row), data.cell.x + padX, priceY, {
          maxWidth: usableWidth,
        });
      },
      willDrawPage: (data) => {
        doc.setFillColor(...bg);
        doc.rect(0, 0, doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight(), "F");
        if (data.pageNumber === 1) drawFirstPageHeader();
      },
      didDrawPage: (data) => {
        const height = doc.internal.pageSize.getHeight();
        doc.setTextColor(...muted);
        doc.setFontSize(7);
        doc.text(`Generated ${new Intl.DateTimeFormat("en-ZA", { dateStyle: "medium", timeStyle: "short" }).format(new Date())}`, 12, height - 6);
        doc.text(`Page ${data.pageNumber}`, pageWidth - 12, height - 6, { align: "right" });
      },
    });

    const stamp = new Date().toISOString().slice(0, 10);
    doc.save(`Morpheus-SOH-${safePdfFilePart(scopeLabel)}-${stamp}.pdf`);
  };

  return (
    <div className="mc-enginePage mc-reviewPage mc-tabPage">
      <div className="mc-enginePage__grid" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--one" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--two" />

      <McPageHeader activeKey="prepared-data" />

      <div className="mc-reviewSubTabs" role="tablist" aria-label="Review views">
        <button
          type="button"
          role="tab"
          aria-selected={reviewSubTab === "review"}
          className={reviewSubTab === "review" ? "is-active" : ""}
          onClick={() => setReviewSubTab("review")}
        >
          Review workspace
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={reviewSubTab === "stock-on-hand"}
          className={reviewSubTab === "stock-on-hand" ? "is-active" : ""}
          onClick={() => setReviewSubTab("stock-on-hand")}
        >
          Stock on Hand
        </button>
      </div>

      {reviewSubTab === "stock-on-hand" ? (
        <section className="mc-sohWorkspace">
          <header className="mc-sohHero">
            <div>
              <div className="mc-enginePage__eyebrow">Live inventory view</div>
              <h2>Stock on Hand</h2>
              <p>View the latest imported or synced SOH by product, Count Group, and selected site scope.</p>
            </div>
            <div className="mc-sohScopeBadge">
              <span>Viewing</span>
              <strong>{activeStoreScope === "ALL" ? "All Sites" : formatStoreLabel(activeStoreScope)}</strong>
            </div>
          </header>

          <div className="mc-sohFilters">
            <SohSearchableMultiSelect
              label="Count Group"
              allLabel="All Count Groups"
              options={sohGroups}
              selected={sohGroupsSelected}
              onChange={setSohGroupsSelected}
              getKey={(group) => group}
              getLabel={(group) => group}
            />
            <SohSearchableMultiSelect
              label="Product"
              allLabel="All products"
              options={sohProductOptions}
              selected={sohProductsSelected}
              onChange={setSohProductsSelected}
              getKey={(row) => row.key}
              getLabel={(row) => `${row.product}${row.sku ? ` · ${row.sku}` : ""}`}
            />
            <div className="mc-sohFilters__search">
              <div className="mc-sohFilters__searchHeader">
                <span>Search</span>
                <button
                  type="button"
                  className="mc-sohClearFilters"
                  onClick={() => {
                    setSohGroupsSelected([]);
                    setSohProductsSelected([]);
                    setSohSearch("");
                    setSohMetricFilter("all");
                    setSohSortBy("product");
                    setSohSortDirection("asc");
                  }}
                >
                  Clear filters
                </button>
              </div>
              <input
                type="search"
                aria-label="Search stock on hand"
                value={sohSearch}
                placeholder="Product, SKU or Count Group"
                onChange={(event) => setSohSearch(event.target.value)}
              />
            </div>
          </div>

          <div className="mc-sohSortBar" aria-label="Stock on Hand sorting">
            <button
              type="button"
              className="mc-sohExportPdf"
              onClick={exportSohPdf}
              disabled={sohLoading || !filteredSohInventory.length}
            >
              Export PDF
            </button>
            <span>Sort by</span>
            <select value={sohSortBy} onChange={(event) => setSohSortBy(event.target.value)} aria-label="Sort stock on hand by">
              <option value="product">Product</option>
              <option value="group">Count Group</option>
            </select>
            <select value={sohSortDirection} onChange={(event) => setSohSortDirection(event.target.value)} aria-label="Stock on hand sort direction">
              <option value="asc">A–Z</option>
              <option value="desc">Z–A</option>
            </select>
          </div>

          <div className="mc-sohMetrics">
            <button type="button" className={sohMetricFilter === "all" ? "is-active" : ""} aria-pressed={sohMetricFilter === "all"} onClick={() => setSohMetricFilter("all")}><span>Products shown</span><strong>{sohLoading ? "···" : sohSummary.products}</strong><small>Show all products</small></button>
            <button type="button" className={sohMetricFilter === "units" ? "is-active" : ""} aria-pressed={sohMetricFilter === "units"} onClick={() => setSohMetricFilter("units")}><span>Total units</span><strong>{sohLoading ? "···" : formatNumber(sohSummary.units)}</strong><small>Filter products in stock</small></button>
            <button type="button" className={sohMetricFilter === "out" ? "is-active" : ""} aria-pressed={sohMetricFilter === "out"} onClick={() => setSohMetricFilter("out")}><span>Out of stock</span><strong>{sohLoading ? "···" : sohSummary.zeroRows}</strong><small>Filter zero stock</small></button>
            <button type="button" className={sohMetricFilter === "low" ? "is-active" : ""} aria-pressed={sohMetricFilter === "low"} onClick={() => setSohMetricFilter("low")}><span>Low stock</span><strong>{sohLoading ? "···" : sohSummary.lowRows}</strong><small>1–5 units at any site</small></button>
          </div>

          {activeStoreScope === "ALL" ? (
            <div className="mc-sohSiteLegend">
              {sohSites.map((site) => {
                const data = sohSiteRows?.[site.scope];
                return (
                  <span key={site.scope} className={data?.available === false ? "is-unavailable" : !data ? "is-loading" : ""}>
                    <i aria-hidden="true" />
                    {site.label}
                    <small>{data?.available === false ? "No snapshot" : !data ? "Checking…" : data?.syncedAt ? formatRevisionTimestamp(data.syncedAt) : "Latest snapshot"}</small>
                  </span>
                );
              })}
            </div>
          ) : null}

          {sohLoading ? (
            <div className="mc-sohLoading"><span className="mc-inlineSpinner" aria-hidden="true" /><strong>Reading latest stock snapshots…</strong></div>
          ) : filteredSohInventory.length ? (
            <>
              <div className="mc-sohTableWrap">
                <table className={`mc-table mc-sohTable ${activeStoreScope === "ALL" ? "is-all-sites" : ""}`}>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Count Group</th>
                      {sohSites.map((site) => <th key={site.scope}>{site.label}</th>)}
                      {activeStoreScope === "ALL" ? <th>Total</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSohInventory.map((row) => {
                      const total = sohSites.reduce((sum, site) => sum + Number(row.sites?.[site.scope] || 0), 0);
                      return (
                        <tr key={row.key}>
                          <td className="mc-sohProductCell"><strong>{row.product}</strong><small>{sohPriceLabel(row)}</small></td>
                          <td>{row.sku || "—"}</td>
                          <td>{row.group}</td>
                          {sohSites.map((site) => (
                            <td key={site.scope} className="mc-sohValue">
                              {sohSiteRows?.[site.scope]?.available === false ? "—" : formatNumber(row.sites?.[site.scope] || 0)}
                            </td>
                          ))}
                          {activeStoreScope === "ALL" ? <td className="mc-sohValue mc-sohValue--total"><strong>{formatNumber(total)}</strong></td> : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mc-sohMobileList">
                {filteredSohInventory.map((row) => (
                  <article key={row.key}>
                    <header><div><strong>{row.product}</strong><small>{sohPriceLabel(row)}</small><span>{row.sku || "No SKU"} · {row.group}</span></div></header>
                    <div>
                      {sohSites.map((site) => (
                        <p key={site.scope}>
                          <span>{site.label}</span>
                          <strong>{sohSiteRows?.[site.scope]?.available === false ? "No snapshot" : formatNumber(row.sites?.[site.scope] || 0)}</strong>
                        </p>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </>
          ) : (
            <div className="mc-engineEmpty mc-sohEmpty">
              <strong>No stock matches this view.</strong>
              <p>Change the Count Group, product, search, stock filter, or selected site scope.</p>
            </div>
          )}

          <footer className="mc-sohFooter">
            <span>SOH reflects the latest active CSV/POS snapshot available for each site.</span>
            <small>PDF export follows the current filters, sort order, site scope, and theme.</small>
          </footer>
        </section>
      ) : (<>

      <section
        className={`mc-reviewHero ${isReady ? "is-ready" : unresolvedCount ? "needs-review" : "is-empty"}`}
      >
        <div className="mc-reviewHero__icon" aria-hidden="true">
          {snapshotLoading
            ? "···"
            : isReady
              ? "✓"
              : unresolvedCount
                ? "!"
                : "○"}
        </div>
        <div className="mc-reviewHero__copy">
          <div className="mc-enginePage__eyebrow">
            {snapshotLoading
              ? "Loading inventory"
              : isReady
                ? "Inventory ready"
                : unresolvedCount
                  ? "Review required"
                  : "Waiting for intake"}
          </div>
          <h2>
            {snapshotLoading
              ? "Reading the active inventory snapshot…"
              : isReady
                ? `${readiness.ready} products are ready for counting.`
                : unresolvedCount
                  ? `${unresolvedCount} products need a decision before counting.`
                  : "No active inventory snapshot yet."}
          </h2>
          <p>
            {snapshotLoading
              ? "Morpheus is applying your mapping, category rules, section ownership, and Count Groups."
              : isReady
                ? "All products have a category, section owner, and Count Group."
                : unresolvedCount
                  ? "Known products remain ready. Unresolved products are isolated and will not enter a count."
                  : "Complete an inventory intake to begin review."}
          </p>
          {latestStorageRevision ? (
            <div className="mc-reviewHero__revision">
              <strong>R{Number(latestStorageRevision.revision || 0)}</strong>
              <span>
                {formatRevisionTimestamp(
                  latestStorageRevision.timestamp ||
                    latestStorageRevision.createdAt ||
                    latestStorageRevision.readiness?.readyAt,
                )}
              </span>
              <small>Revision about to be prepared</small>
            </div>
          ) : null}
        </div>
        <div className="mc-reviewHero__actions">
          {unresolvedCount > 0 ? (
            <button
              className="mc-engineBtn mc-engineBtn--primary"
              type="button"
              onClick={() => {
                setActiveFilter("all");
                setExceptionStatus("all");
                setShowProducts(true);
                scrollToReviewSection("mc-exception-workspace");
              }}
            >
              Review exceptions
            </button>
          ) : null}
          <button
            className="mc-engineBtn mc-engineBtn--ghost"
            type="button"
            onClick={() => {
              if (showProducts) {
                setShowProducts(false);
                return;
              }
              setShowProducts(true);
              scrollToReviewSection("mc-product-review");
            }}
            disabled={!hasInventory}
          >
            {showProducts ? "Hide products" : "View products"}
          </button>
        </div>
      </section>

      <section className="mc-reviewMetrics">
        <button
          type="button"
          className={
            activeFilter === "ready" ? "is-active is-ready" : "is-ready"
          }
          onClick={() => {
            setActiveFilter("ready");
            setShowProducts(true);
            scrollToReviewSection("mc-product-review");
          }}
        >
          <span>Ready</span>
          <strong>{readiness.ready}</strong>
          <small>Operational products</small>
        </button>
        <button
          type="button"
          className={
            activeFilter === "category" ? "is-active is-warning" : "is-warning"
          }
          onClick={() => {
            setActiveFilter("category");
            setShowProducts(true);
            scrollToReviewSection("mc-product-review");
          }}
        >
          <span>Need category rules</span>
          <strong>{readiness.category}</strong>
          <small>Unmatched products</small>
        </button>
        <button
          type="button"
          className={
            activeFilter === "section" ? "is-active is-warning" : "is-warning"
          }
          onClick={() => {
            setActiveFilter("section");
            setShowProducts(true);
            scrollToReviewSection("mc-product-review");
          }}
        >
          <span>Need section</span>
          <strong>{readiness.section}</strong>
          <small>Ownership missing</small>
        </button>
        <button
          type="button"
          className={
            activeFilter === "group" ? "is-active is-warning" : "is-warning"
          }
          onClick={() => {
            setActiveFilter("group");
            setShowProducts(true);
            scrollToReviewSection("mc-product-review");
          }}
        >
          <span>Need Count Group</span>
          <strong>{readiness.group}</strong>
          <small>Group missing</small>
        </button>
        <button
          type="button"
          className={activeFilter === "ignored" ? "is-active" : ""}
          onClick={() => {
            setActiveFilter("ignored");
            setShowProducts(true);
            scrollToReviewSection("mc-product-review");
          }}
        >
          <span>Ignored</span>
          <strong>{readiness.ignored}</strong>
          <small>Excluded by design</small>
        </button>
      </section>

      <section className="mc-reviewAssurance">
        <button
          type="button"
          className={`mc-reviewAssurance__action ${pendingDuplicateCount ? "has-warning" : ""}`}
          onClick={() => pendingDuplicateCount && setDuplicateReviewOpen(true)}
          disabled={!pendingDuplicateCount}
          aria-label={pendingDuplicateCount ? "Review duplicate SKUs" : "No duplicate SKUs detected"}
        >
          <span className={pendingDuplicateCount ? "is-warning" : "is-ok"}>
            {pendingDuplicateCount ? "!" : "✓"}
          </span>
          <div>
            <strong>Duplicate SKUs</strong>
            <p>
              {pendingDuplicateCount
                ? `${pendingDuplicateCount} duplicate rows require attention. Click to review.`
                : duplicateSummary.count
                  ? "Duplicate rows reviewed and accepted."
                  : "No duplicate rows detected."}
            </p>
          </div>
        </button>
        <article>
          <span className={convertedCount ? "is-ok" : "is-neutral"}>
            {convertedCount ? "✓" : "—"}
          </span>
          <div>
            <strong>Unit intelligence</strong>
            <p>
              {convertedCount
                ? `${convertedCount} products use quantity conversion.`
                : "No unit conversions applied."}
            </p>
          </div>
        </article>
        <article>
          <span className={engine.mapping?.systemSoh ? "is-ok" : "is-warning"}>
            {engine.mapping?.systemSoh ? "✓" : "!"}
          </span>
          <div>
            <strong>Inventory source</strong>
            <p>
              {engine.mapping?.systemSoh || "Site inventory column is not set."}
            </p>
          </div>
        </article>
        <article>
          <span
            className={
              unresolvedCount
                ? "is-warning"
                : hasInventory
                  ? "is-ok"
                  : "is-neutral"
            }
          >
            {unresolvedCount ? "!" : hasInventory ? "✓" : "—"}
          </span>
          <div>
            <strong>Readiness gate</strong>
            <p>
              {unresolvedCount
                ? "Unresolved products are blocked from sessions."
                : hasInventory
                  ? "Inventory can move into count preparation."
                  : "Waiting for inventory."}
            </p>
          </div>
        </article>
      </section>

      {duplicateReviewOpen ? (
        <div className="mc-modalBackdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setDuplicateReviewOpen(false); }}>
          <section className="mc-duplicateReviewModal" role="dialog" aria-modal="true" aria-labelledby="mc-duplicate-review-title">
            <header>
              <div>
                <span className="mc-kicker">Inventory assurance</span>
                <h2 id="mc-duplicate-review-title">Review duplicate SKUs</h2>
                <p>Morpheus has merged matching rows into one countable product. Confirm intentional duplicates or exclude a product from counting.</p>
              </div>
              <button type="button" className="mc-engineBtn" onClick={() => setDuplicateReviewOpen(false)}>Close</button>
            </header>
            <div className="mc-duplicateReviewModal__summary">
              <strong>{pendingDuplicateItems.length} product group{pendingDuplicateItems.length === 1 ? "" : "s"}</strong>
              <span>{pendingDuplicateCount} extra row{pendingDuplicateCount === 1 ? "" : "s"} merged</span>
            </div>
            <div className="mc-duplicateReviewModal__list">
              {pendingDuplicateItems.map((item) => (
                <article key={item.duplicateKey}>
                  <div>
                    <strong>{item.product || "Unnamed product"}</strong>
                    <span>SKU: {item.sku || "No SKU"}</span>
                    <small>{item.rowsFound} rows found · {item.merged} merged</small>
                  </div>
                  <div className="mc-duplicateReviewModal__actions">
                    <button type="button" className="mc-engineBtn" onClick={() => ignoreDuplicateItem(item)}>Ignore product</button>
                    <button type="button" className="mc-engineBtn mc-engineBtn--primary" disabled={duplicateDecisionSaving} onClick={() => acceptDuplicateItems([item])}>{duplicateDecisionSaving ? "Saving…" : "Accept merged rows"}</button>
                  </div>
                </article>
              ))}
            </div>
            <footer>
              <button type="button" className="mc-engineBtn" onClick={() => setDuplicateReviewOpen(false)}>Done</button>
              <button type="button" className="mc-engineBtn mc-engineBtn--primary" disabled={duplicateDecisionSaving} onClick={async () => { const saved = await acceptDuplicateItems(pendingDuplicateItems); if (saved) setDuplicateReviewOpen(false); }}>{duplicateDecisionSaving ? "Saving decisions…" : "Accept all merged rows"}</button>
            </footer>
          </section>
        </div>
      ) : null}

      {hasInventory ? (
        <section className="mc-intelligenceCenter">
          <div className="mc-intelligenceCenter__head mc-intelligenceCenter__head--executive">
            <div>
              <div className="mc-enginePage__eyebrow">
                Product intelligence hub
              </div>
              <h3>
                {unresolvedCount
                  ? "Inventory requires operational decisions."
                  : "Inventory is operationally understood."}
              </h3>
              <p>
                {unresolvedCount
                  ? `${unresolvedCount} decisions remain before this inventory can become fully operational.`
                  : "Every active product is understood, intentionally ignored, and ready for count preparation."}
              </p>
            </div>
            <div
              className={`mc-intelligenceScore ${intelligenceScore === 100 ? "is-complete" : ""}`}
            >
              <strong>{intelligenceScore}%</strong>
              <span>Operational readiness</span>
            </div>
          </div>
          <div
            className="mc-intelligenceExecutiveGrid"
            aria-label="Inventory intelligence summary"
          >
            <article>
              <span>Products understood</span>
              <strong>
                {readiness.ready + readiness.ignored}
                <small> / {reviewedRows.length}</small>
              </strong>
              <p>Ready or intentionally excluded</p>
            </article>
            <article>
              <span>Operational readiness</span>
              <strong>{intelligenceScore}%</strong>
              <p>Trusted inventory coverage</p>
            </article>
            <article>
              <span>Rules learned</span>
              <strong>{learnedRuleCount}</strong>
              <p>Active normalization rules</p>
            </article>
            <article>
              <span>Patterns detected</span>
              <strong>{patternSuggestions.length}</strong>
              <p>Bulk decision opportunities</p>
            </article>
            <article className={unresolvedCount ? "is-warning" : "is-ready"}>
              <span>Exceptions remaining</span>
              <strong>{unresolvedCount}</strong>
              <p>
                {unresolvedCount
                  ? "Blocked from count sessions"
                  : "No unresolved products"}
              </p>
            </article>
            <article>
              <span>Outputs created</span>
              <strong>{outputCount}</strong>
              <p>Controlled inventory concepts</p>
            </article>
          </div>
          {reviewNotice ? (
            <div className="mc-intelligenceNotice">
              <span>✓ {reviewNotice}</span>
              {undoEngine ? (
                <button type="button" onClick={undoLastDecision}>
                  Undo
                </button>
              ) : null}
            </div>
          ) : null}
          {unresolvedCount ? (
            <>
              {patternSuggestions.length ? (
                <>
                  <div
                    className={`mc-bulkPatternBar ${selectedPatterns.length ? "is-active" : ""}`}
                  >
                    <label className="mc-bulkSelectAll">
                      <input
                        type="checkbox"
                        checked={allPatternsSelected}
                        onChange={toggleAllPatterns}
                      />
                      <span>
                        {allPatternsSelected
                          ? "All patterns selected"
                          : "Select all patterns"}
                      </span>
                    </label>
                    <div className="mc-bulkPatternSummary">
                      <strong>{selectedPatterns.length}</strong> patterns
                      selected
                      <span aria-hidden="true">•</span>
                      <strong>{selectedPatternProducts}</strong> affected
                      products
                    </div>
                    <div className="mc-bulkPatternActions">
                      <button
                        type="button"
                        className="mc-engineBtn mc-engineBtn--primary"
                        disabled={!selectedPatterns.length}
                        onClick={openBulkLearn}
                      >
                        Learn selected
                      </button>
                      <button
                        type="button"
                        className="mc-engineBtn mc-engineBtn--ghost"
                        disabled={!selectedPatterns.length}
                        title="Bulk ignore is enabled in Phase C"
                      >
                        Ignore selected
                      </button>
                      <button
                        type="button"
                        className="mc-engineBtn mc-engineBtn--ghost"
                        disabled={!selectedPatterns.length}
                        title="Bulk review is enabled in Phase E"
                      >
                        Review selected
                      </button>
                      <button
                        type="button"
                        className="mc-engineBtn mc-engineBtn--ghost"
                        disabled={!selectedPatterns.length}
                        onClick={() => setSelectedPatternKeys([])}
                      >
                        Clear selection
                      </button>
                    </div>
                  </div>
                  <div className="mc-patternGrid">
                    {patternSuggestions.map((item) => {
                      const isSelected = selectedPatternKeys.includes(
                        patternKey(item),
                      );
                      return (
                        <article
                          key={`${item.status}-${item.keyword}-${item.category}`}
                          className={isSelected ? "is-selected" : ""}
                        >
                          <div className="mc-patternCard__top">
                            <label
                              className="mc-patternSelect"
                              title={`Select ${titleCase(item.keyword)} pattern`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => togglePatternSelection(item)}
                              />
                              <span aria-hidden="true">✓</span>
                            </label>
                            <span className="mc-intelligenceLabel">
                              Detected pattern
                            </span>
                            <span
                              className={`mc-confidenceBadge is-${item.confidence >= 90 ? "high" : item.confidence >= 70 ? "medium" : "low"}`}
                            >
                              {item.confidence}% confidence
                            </span>
                          </div>
                          <h4>
                            {item.rows.length} products contain “
                            {titleCase(item.keyword)}”
                          </h4>
                          <dl>
                            <div>
                              <dt>Suggested category</dt>
                              <dd>
                                {titleCase(item.category) || "Review required"}
                              </dd>
                            </div>
                            <div>
                              <dt>Section</dt>
                              <dd>
                                {item.sectionId
                                  ? formatSectionLabel(item.sectionId)
                                  : "Not assigned"}
                              </dd>
                            </div>
                            <div>
                              <dt>Count Group</dt>
                              <dd>{item.groupName || "Not assigned"}</dd>
                            </div>
                          </dl>
                          <div className="mc-patternActions">
                            <button
                              type="button"
                              className="mc-engineBtn mc-engineBtn--ghost"
                              onClick={() => {
                                setPatternFilter(item.keyword);
                                setExceptionSearch("");
                                scrollToReviewSection("mc-exception-workspace");
                              }}
                            >
                              Review products
                            </button>
                            <button
                              type="button"
                              className="mc-engineBtn mc-engineBtn--primary"
                              onClick={() => openBulkLearn([item])}
                            >
                              Learn rule
                            </button>
                            <button
                              type="button"
                              className="mc-engineBtn mc-engineBtn--ghost"
                              onClick={() => ignoreRows(item.rows)}
                            >
                              Ignore pattern
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </>
              ) : null}
              <div className="mc-exceptionList" id="mc-exception-workspace">
                <div className="mc-exceptionList__title">
                  <div>
                    <h4>Exceptions requiring a decision</h4>
                    <p>
                      Accept a suggestion, create a reusable rule, or
                      intentionally ignore the item.
                    </p>
                  </div>
                  <span>
                    {filteredExceptionRows.length} shown ·{" "}
                    {exceptionRows.length} unresolved
                  </span>
                </div>
                <div className="mc-exceptionFilters">
                  <input
                    value={exceptionSearch}
                    onChange={(event) => {
                      setExceptionSearch(event.target.value);
                      setPatternFilter("");
                    }}
                    placeholder="Search exceptions, products, SKU, pattern…"
                  />
                  <select
                    value={exceptionStatus}
                    onChange={(event) => setExceptionStatus(event.target.value)}
                  >
                    <option value="all">All reasons</option>
                    <option value="category">Need category rule</option>
                    <option value="section">Need section</option>
                    <option value="group">Need Count Group</option>
                  </select>
                  <select
                    value={exceptionConfidence}
                    onChange={(event) =>
                      setExceptionConfidence(event.target.value)
                    }
                  >
                    <option value="all">All confidence</option>
                    <option value="high">High 90%+</option>
                    <option value="medium">Medium 70–89%</option>
                    <option value="low">Low below 70%</option>
                  </select>
                  <button
                    type="button"
                    className="mc-engineBtn mc-engineBtn--ghost"
                    onClick={() => {
                      setExceptionSearch("");
                      setPatternFilter("");
                      setExceptionStatus("all");
                      setExceptionConfidence("all");
                    }}
                  >
                    Clear
                  </button>
                </div>
                {patternFilter ? (
                  <div className="mc-activePatternFilter">
                    Filtered to pattern:{" "}
                    <strong>{titleCase(patternFilter)}</strong>
                    <button type="button" onClick={() => setPatternFilter("")}>
                      ×
                    </button>
                  </div>
                ) : null}
                {filteredExceptionRows.map((row) => {
                  const suggestion = buildSuggestion(row, engine);
                  return (
                    <article key={row.id}>
                      <div className="mc-exceptionProduct">
                        <strong>{row.product}</strong>
                        <small>
                          {row.sku || "No SKU"} ·{" "}
                          {row.sourceCategory || "No source category"}
                        </small>
                      </div>
                      <button
                        type="button"
                        className="mc-exceptionReason mc-exceptionReason--button"
                        onClick={() => openRuleModal([row], suggestion)}
                      >
                        <span>{row.readiness?.reason}</span>
                        <small>
                          {suggestion.category
                            ? `${suggestion.confidence}% confidence`
                            : "Manual review"}
                        </small>
                      </button>
                      <div className="mc-exceptionSuggestion">
                        <b>
                          {titleCase(suggestion.category) ||
                            "No category suggestion"}
                        </b>
                        <small>
                          {suggestion.sectionId
                            ? formatSectionLabel(suggestion.sectionId)
                            : "Section missing"}{" "}
                          · {suggestion.groupName || "Count Group missing"}
                        </small>
                      </div>
                      <div className="mc-exceptionActions">
                        <button
                          type="button"
                          onClick={() => openRuleModal([row], suggestion)}
                        >
                          Accept & learn
                        </button>
                        <button type="button" onClick={() => ignoreRows([row])}>
                          Ignore
                        </button>
                      </div>
                    </article>
                  );
                })}
                {!filteredExceptionRows.length ? (
                  <div className="mc-exceptionMore">
                    No exceptions match the current filters.
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <div className="mc-intelligenceComplete">
              <span>✓</span>
              <div>
                <strong>Inventory trusted.</strong>
                <p>
                  Every countable product has a category, section owner and
                  Count Group.
                </p>
              </div>
            </div>
          )}
        </section>
      ) : null}

      {bulkLearnOpen ? (
        <div
          className="mc-bulkLearnBackdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget &&
              bulkLearnStage !== "saving"
            )
              setBulkLearnOpen(false);
          }}
        >
          <section
            ref={bulkLearnModalRef}
            className="mc-bulkLearnModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mc-bulk-learn-title"
          >
            <header className="mc-bulkLearnModal__head">
              <div>
                <span>{selectedPatterns.length === 1 ? "Rule learning" : "Bulk rule learning"}</span>
                <h3 id="mc-bulk-learn-title">
                  {bulkLearnStage === "success"
                    ? "Inventory Intelligence Updated"
                    : bulkLearnStage === "saving"
                      ? "Learning inventory…"
                      : bulkLearnStage === "review"
                        ? "Review Inventory Changes"
                        : selectedPatterns.length === 1
                          ? "Learn Inventory Rule"
                          : "Learn Multiple Inventory Rules"}
                </h3>
                <p>
                  {bulkCommitResult
                    ? `${bulkCommitResult.rules} patterns selected · ${bulkCommitResult.products} products affected`
                    : `${selectedPatterns.length} pattern${selectedPatterns.length === 1 ? "" : "s"} selected · ${selectedPatternProducts} products affected`}
                </p>
              </div>
              {bulkLearnStage !== "saving" ? (
                <button type="button" onClick={() => setBulkLearnOpen(false)}>
                  ×
                </button>
              ) : null}
            </header>

            {bulkLearnStage === "success" ? (
              <section className="mc-bulkSuccess">
                <div className="mc-bulkSuccess__icon">✓</div>
                <h4>
                  {bulkCommitResult?.products || 0} products became operational
                </h4>
                <p>
                  {bulkCommitResult?.rules || 0} reusable rules were written to
                  Inventory Configuration.
                  {Number(bulkCommitResult?.remaining || 0) > 0
                    ? ` ${bulkCommitResult.remaining} products still require attention.`
                    : ""}
                </p>
                {bulkTransactionId ? (
                  <small className="mc-bulkSuccess__transaction">
                    Transaction recorded for History and Undo.
                  </small>
                ) : null}
                <div className="mc-bulkSuccess__readiness">
                  <span>Operational readiness</span>
                  <strong>
                    {bulkCommitResult?.fromReadiness ?? intelligenceScore}%{" "}
                    <small>
                      → {bulkCommitResult?.toReadiness ?? projectedReadiness}%
                    </small>
                  </strong>
                </div>
                <button
                  type="button"
                  className="mc-engineBtn mc-engineBtn--primary"
                  onClick={() => {
                    setSelectedPatternKeys([]);
                    setBulkLearnOpen(false);
                  }}
                >
                  Continue
                </button>
              </section>
            ) : bulkLearnStage === "saving" ? (
              <section className="mc-bulkPipeline">
                <p>
                  Morpheus is committing these decisions to the Inventory
                  Configuration source of truth.
                </p>
                <button
                  type="button"
                  className="mc-engineBtn"
                  onClick={() => {
                    bulkLearnCancelRef.current = true;
                    setBulkLearnError("Cancelling inventory learning…");
                  }}
                >
                  Cancel learning
                </button>
                <div>
                  {bulkLearnProgress.map((step) => (
                    <article key={step.label} className={`is-${step.status}`}>
                      <span aria-hidden="true">
                        {step.status === "done" ? "✓" : "↻"}
                      </span>
                      <strong>
                        {step.label}
                        {step.status === "done" ? "" : "…"}
                      </strong>
                    </article>
                  ))}
                </div>
              </section>
            ) : (
              <>
                <div className="mc-bulkLearnModal__impact">
                  <article>
                    <span>Patterns selected</span>
                    <strong>{selectedPatterns.length}</strong>
                  </article>
                  <article>
                    <span>Products affected</span>
                    <strong>{selectedPatternProducts}</strong>
                  </article>
                  <article>
                    <span>Operational readiness</span>
                    <strong>
                      {intelligenceScore}%{" "}
                      <small>→ {projectedReadiness}%</small>
                    </strong>
                  </article>
                </div>

                {bulkLearnStage === "configure" ? (
                  <>
                    <section className="mc-bulkLearnModal__selection mc-bulkLearnModal__configuration">
                      <div className="mc-bulkLearnModal__sectionHead">
                        <div>
                          <span>Configure patterns</span>
                          <p>
                            Morpheus has prefilled confident decisions. Deselect
                            any pattern you do not want to learn now, then
                            complete the remaining patterns.
                          </p>
                        </div>
                        <strong>
                          {completedBulkDrafts} / {selectedPatterns.length}{" "}
                          complete
                        </strong>
                      </div>
                      <div className="mc-bulkLearnModal__patternList mc-bulkLearnModal__configList">
                        {selectedPatterns.map((item) => {
                          const key = patternKey(item);
                          const draft = bulkRuleDrafts[key] || {};
                          const expanded = expandedBulkPattern === key;
                          const valid = bulkDraftIsValid(draft);
                          return (
                            <article
                              key={`bulk-${key}`}
                              className={`mc-bulkConfigCard ${expanded ? "is-expanded" : ""} ${valid ? "is-complete" : "is-incomplete"}`}
                            >
                              <div className="mc-bulkConfigCard__summaryRow">
                                <button
                                  type="button"
                                  className="mc-bulkConfigCard__select"
                                  onClick={() =>
                                    removePatternFromBulkLearn(item)
                                  }
                                  title="Remove this pattern from the current bulk learning transaction"
                                  aria-label={`Deselect ${titleCase(item.keyword)}`}
                                >
                                  ✓
                                </button>
                                <button
                                  type="button"
                                  className="mc-bulkConfigCard__summary"
                                  onClick={() =>
                                    setExpandedBulkPattern(expanded ? "" : key)
                                  }
                                  aria-expanded={expanded}
                                >
                                  <span>{valid ? "✓" : "!"}</span>
                                  <div>
                                    <strong>{titleCase(item.keyword)}</strong>
                                    <small>
                                      {item.rows.length} products ·{" "}
                                      {item.confidence}% confidence
                                    </small>
                                  </div>
                                  <em>
                                    {valid
                                      ? `${draft.output} · ${draft.sectionMode === "new" ? draft.sectionName : formatSectionLabel(draft.sectionId)} · ${draft.groupName}`
                                      : "Review required"}
                                  </em>
                                  <b>{expanded ? "−" : "+"}</b>
                                </button>
                              </div>
                              {expanded ? (
                                <div className="mc-bulkConfigCard__body">
                                  <div className="mc-bulkConfigCard__fields">
                                    <label>
                                      <span>Output</span>
                                      <select
                                        value={draft.outputMode || "new"}
                                        onChange={(event) => {
                                          const nextMode = event.target.value;
                                          const currentOutput = clean(draft.output);
                                          updateBulkRuleDraft(key, {
                                            outputMode: nextMode,
                                            output:
                                              nextMode === "existing"
                                                ? knownOutputOptions.find(
                                                    (output) =>
                                                      clean(output) ===
                                                      currentOutput,
                                                  ) || knownOutputOptions[0] || ""
                                                : knownOutputs.has(currentOutput)
                                                  ? ""
                                                  : draft.output || "",
                                          });
                                        }}
                                      >
                                        <option value="new">
                                          Create new output
                                        </option>
                                        <option
                                          value="existing"
                                          disabled={!knownOutputOptions.length}
                                        >
                                          Use existing output
                                        </option>
                                      </select>
                                    </label>
                                    {draft.outputMode === "existing" ? (
                                      <label>
                                        <span>Existing output</span>
                                        <select
                                          value={draft.output || ""}
                                          onChange={(event) =>
                                            updateBulkRuleDraft(key, {
                                              output: event.target.value,
                                            })
                                          }
                                        >
                                          <option value="">
                                            Choose existing output
                                          </option>
                                          {knownOutputOptions.map((output) => (
                                            <option key={output} value={output}>
                                              {output}
                                            </option>
                                          ))}
                                        </select>
                                      </label>
                                    ) : (
                                      <label>
                                        <span>New output name</span>
                                        <input
                                          value={draft.output || ""}
                                          onChange={(event) =>
                                            updateBulkRuleDraft(key, {
                                              output: event.target.value,
                                            })
                                          }
                                          placeholder="Normalized output"
                                        />
                                      </label>
                                    )}
                                    <label>
                                      <span>Section</span>
                                      <select
                                        value={draft.sectionMode || "existing"}
                                        onChange={(event) =>
                                          updateBulkRuleDraft(key, {
                                            sectionMode: event.target.value,
                                          })
                                        }
                                      >
                                        <option value="existing">
                                          Use existing section
                                        </option>
                                        <option value="new">
                                          Create new section
                                        </option>
                                      </select>
                                    </label>
                                    {draft.sectionMode === "new" ? (
                                      <label>
                                        <span>New section name</span>
                                        <input
                                          value={draft.sectionName || ""}
                                          onChange={(event) =>
                                            updateBulkRuleDraft(key, {
                                              sectionName: event.target.value,
                                            })
                                          }
                                        />
                                      </label>
                                    ) : (
                                      <label>
                                        <span>Existing section</span>
                                        <select
                                          value={draft.sectionId || ""}
                                          onChange={(event) =>
                                            updateBulkRuleDraft(key, {
                                              sectionId: event.target.value,
                                            })
                                          }
                                        >
                                          <option value="">
                                            Choose section
                                          </option>
                                          {sections.map((section) => (
                                            <option
                                              key={
                                                section.sectionId || section.id
                                              }
                                              value={normalizeSectionId(
                                                section.sectionId ||
                                                  section.id ||
                                                  section.name,
                                              )}
                                            >
                                              {section.name}
                                            </option>
                                          ))}
                                        </select>
                                      </label>
                                    )}
                                    <label>
                                      <span>Count Group</span>
                                      <select
                                        value={draft.groupMode || "existing"}
                                        onChange={(event) =>
                                          updateBulkRuleDraft(key, {
                                            groupMode: event.target.value,
                                          })
                                        }
                                      >
                                        <option value="existing">
                                          Use existing Count Group
                                        </option>
                                        <option value="new">
                                          Create new Count Group
                                        </option>
                                      </select>
                                    </label>
                                    {draft.groupMode === "new" ? (
                                      <label>
                                        <span>New Count Group name</span>
                                        <input
                                          value={draft.groupName || ""}
                                          onChange={(event) =>
                                            updateBulkRuleDraft(key, {
                                              groupName: event.target.value,
                                            })
                                          }
                                        />
                                      </label>
                                    ) : (
                                      <label>
                                        <span>Existing Count Group</span>
                                        <select
                                          value={draft.groupName || ""}
                                          onChange={(event) =>
                                            updateBulkRuleDraft(key, {
                                              groupName: event.target.value,
                                            })
                                          }
                                        >
                                          <option value="">
                                            Choose Count Group
                                          </option>
                                          {configuredGroups.map((group) => (
                                            <option
                                              key={group.name}
                                              value={group.name}
                                            >
                                              {group.name}
                                            </option>
                                          ))}
                                        </select>
                                      </label>
                                    )}
                                  </div>
                                  <div className="mc-bulkConfigCard__preview">
                                    <span>Live preview</span>
                                    <strong>{item.rows.length} products</strong>
                                    <div>
                                      <b>Current</b>
                                      <em>
                                        {titleCase(item.category) ||
                                          "Review required"}{" "}
                                        ·{" "}
                                        {item.sectionId
                                          ? formatSectionLabel(item.sectionId)
                                          : "No section"}{" "}
                                        · {item.groupName || "No valid group"}
                                      </em>
                                      <i>→</i>
                                      <b>New</b>
                                      <em>
                                        {draft.output || "Output required"} ·{" "}
                                        {draft.sectionMode === "new"
                                          ? draft.sectionName ||
                                            "Section required"
                                          : formatSectionLabel(
                                              draft.sectionId,
                                            ) || "Section required"}{" "}
                                        ·{" "}
                                        {draft.groupName ||
                                          "Count Group required"}
                                      </em>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                            </article>
                          );
                        })}
                      </div>
                    </section>
                    <section className="mc-bulkLearnModal__summary">
                      <article>
                        <span>Rules to create</span>
                        <strong>{selectedPatterns.length}</strong>
                      </article>
                      <article>
                        <span>Products affected</span>
                        <strong>{selectedPatternProducts}</strong>
                      </article>
                      <article>
                        <span>New outputs</span>
                        <strong>{newOutputCount}</strong>
                      </article>
                      <article>
                        <span>Existing outputs</span>
                        <strong>{existingOutputCount}</strong>
                      </article>
                      <article>
                        <span>New sections</span>
                        <strong>{newSectionCount}</strong>
                      </article>
                      <article>
                        <span>Readiness</span>
                        <strong>
                          {intelligenceScore}%{" "}
                          <small>→ {projectedReadiness}%</small>
                        </strong>
                      </article>
                    </section>
                    <footer className="mc-bulkLearnModal__actions">
                      <button
                        type="button"
                        className="mc-engineBtn mc-engineBtn--ghost"
                        onClick={() => setBulkLearnOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="mc-engineBtn mc-engineBtn--primary"
                        disabled={!allBulkDraftsValid}
                        onClick={() => setBulkLearnStage("review")}
                      >
                        Continue to review
                      </button>
                    </footer>
                  </>
                ) : (
                  <>
                    <section className="mc-bulkReviewIntro">
                      <h4>Ready to learn inventory.</h4>
                      <p>
                        No products will be lost. Morpheus will create reusable
                        configuration and normalize the active snapshot.
                      </p>
                    </section>
                    <section className="mc-bulkCommitChecklist">
                      <h4>Morpheus will</h4>
                      <div>
                        <span>✓ Create {newOutputCount} outputs</span>
                        <span>✓ Use {existingOutputCount} existing outputs</span>
                        <span>✓ Create {newSectionCount} sections</span>
                        <span>✓ Create {newGroupCount} Count Groups</span>
                        <span>✓ Create {selectedPatterns.length} rules</span>
                        <span>
                          ✓ Normalize {selectedPatternProducts} products
                        </span>
                        <span>✓ Recalculate operational readiness</span>
                      </div>
                    </section>
                    <section className="mc-bulkReviewCards">
                      {reviewBulkChanges.map((change) => (
                        <article key={change.key}>
                          <header>
                            <div>
                              <span>Pattern</span>
                              <strong>{change.keyword}</strong>
                              <small>
                                {change.products} products · {change.confidence}
                                % confidence
                              </small>
                            </div>
                          </header>
                          <div>
                            <p>
                              <span>Output</span>
                              <strong>{change.output}</strong>
                              <em
                                className={
                                  change.outputIsNew ? "is-new" : "is-existing"
                                }
                              >
                                {change.outputIsNew ? "New" : "Existing"}
                              </em>
                            </p>
                            <p>
                              <span>Section</span>
                              <strong>{change.sectionName}</strong>
                              <em
                                className={
                                  change.sectionIsNew ? "is-new" : "is-existing"
                                }
                              >
                                {change.sectionIsNew ? "New" : "Existing"}
                              </em>
                            </p>
                            <p>
                              <span>Count Group</span>
                              <strong>{change.groupName}</strong>
                              <em
                                className={
                                  change.groupIsNew ? "is-new" : "is-existing"
                                }
                              >
                                {change.groupIsNew ? "New" : "Existing"}
                              </em>
                            </p>
                          </div>
                        </article>
                      ))}
                    </section>
                    {bulkLearnError ? (
                      <div className="mc-bulkLearnError">{bulkLearnError}</div>
                    ) : null}
                    <footer className="mc-bulkLearnModal__actions">
                      <button
                        type="button"
                        className="mc-engineBtn mc-engineBtn--ghost"
                        onClick={() => setBulkLearnStage("configure")}
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        className="mc-engineBtn mc-engineBtn--primary"
                        onClick={() => setBulkConfirmOpen(true)}
                      >
                        Learn Inventory
                      </button>
                    </footer>
                  </>
                )}
              </>
            )}
          </section>
        </div>
      ) : null}

      {bulkConfirmOpen ? (
        <div
          className="mc-bulkConfirmBackdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setBulkConfirmOpen(false);
          }}
        >
          <section
            className="mc-bulkConfirmModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mc-bulk-confirm-title"
          >
            <header>
              <div>
                <span>Final confirmation</span>
                <h3 id="mc-bulk-confirm-title">Learn Inventory?</h3>
                <p>
                  Morpheus is about to permanently teach these decisions to
                  Inventory Configuration.
                </p>
              </div>
              <button type="button" onClick={() => setBulkConfirmOpen(false)}>
                ×
              </button>
            </header>
            <div className="mc-bulkConfirmSummary">
              <article>
                <span>Rules</span>
                <strong>{selectedPatterns.length}</strong>
              </article>
              <article>
                <span>Outputs</span>
                <strong>{newOutputCount}</strong>
              </article>
              <article>
                <span>Sections</span>
                <strong>{newSectionCount}</strong>
              </article>
              <article>
                <span>Count Groups</span>
                <strong>{newGroupCount}</strong>
              </article>
              <article>
                <span>Products</span>
                <strong>{selectedPatternProducts}</strong>
              </article>
              <article>
                <span>Readiness</span>
                <strong>
                  {intelligenceScore}% <small>→ {projectedReadiness}%</small>
                </strong>
              </article>
            </div>
            <div className="mc-bulkConfirmNotice">
              <strong>This is the commit step.</strong>
              <p>
                No changes are written until you confirm below. A transaction
                record will be created for future History and Undo support.
              </p>
            </div>
            <footer>
              <button
                type="button"
                className="mc-engineBtn mc-engineBtn--ghost"
                onClick={() => setBulkConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="mc-engineBtn mc-engineBtn--primary"
                onClick={commitBulkLearning}
              >
                Yes, Learn Inventory
              </button>
            </footer>
          </section>
        </div>
      ) : null}

      {ruleModal ? (
        <div
          className="mc-ruleModalBackdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setRuleModal(null);
          }}
        >
          <section
            className="mc-ruleModal"
            role="dialog"
            aria-modal="true"
            aria-label="Learn inventory rule"
          >
            <div className="mc-ruleModal__head">
              <div>
                <span>Learn inventory rule</span>
                <h3>{titleCase(ruleModal.suggestion?.keyword)}</h3>
                <p>
                  {ruleModal.rows.length} product
                  {ruleModal.rows.length === 1 ? "" : "s"} affected
                </p>
              </div>
              <button type="button" onClick={() => setRuleModal(null)}>
                ×
              </button>
            </div>
            <div className="mc-ruleModal__grid">
              <label>
                <span>Category</span>
                <input
                  value={ruleDraft.category}
                  onChange={(event) =>
                    setRuleDraft((current) => ({
                      ...current,
                      category: event.target.value,
                    }))
                  }
                  placeholder="Category name"
                />
              </label>
              <label>
                <span>Section</span>
                <select
                  value={ruleDraft.sectionMode}
                  onChange={(event) =>
                    setRuleDraft((current) => ({
                      ...current,
                      sectionMode: event.target.value,
                    }))
                  }
                >
                  <option value="existing">Use existing section</option>
                  <option value="new">Create new section</option>
                </select>
              </label>
              {ruleDraft.sectionMode === "existing" ? (
                <label>
                  <span>Existing section</span>
                  <select
                    value={ruleDraft.sectionId}
                    onChange={(event) =>
                      setRuleDraft((current) => ({
                        ...current,
                        sectionId: event.target.value,
                      }))
                    }
                  >
                    <option value="">Choose section</option>
                    {sections.map((section) => (
                      <option
                        key={section.sectionId || section.id}
                        value={normalizeSectionId(
                          section.sectionId || section.id || section.name,
                        )}
                      >
                        {section.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <label>
                  <span>New section name</span>
                  <input
                    value={ruleDraft.sectionName}
                    onChange={(event) =>
                      setRuleDraft((current) => ({
                        ...current,
                        sectionName: event.target.value,
                      }))
                    }
                  />
                </label>
              )}
              <label>
                <span>Count Group</span>
                <select
                  value={ruleDraft.groupMode}
                  onChange={(event) =>
                    setRuleDraft((current) => ({
                      ...current,
                      groupMode: event.target.value,
                    }))
                  }
                >
                  <option value="existing">Use existing Count Group</option>
                  <option value="new">Create new Count Group</option>
                </select>
              </label>
              {ruleDraft.groupMode === "existing" ? (
                <label>
                  <span>Existing Count Group</span>
                  <select
                    value={ruleDraft.groupName}
                    onChange={(event) =>
                      setRuleDraft((current) => ({
                        ...current,
                        groupName: event.target.value,
                      }))
                    }
                  >
                    <option value="">Choose Count Group</option>
                    {configuredGroups.map((group) => (
                      <option key={group.name} value={group.name}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <label>
                  <span>New Count Group name</span>
                  <input
                    value={ruleDraft.groupName}
                    onChange={(event) =>
                      setRuleDraft((current) => ({
                        ...current,
                        groupName: event.target.value,
                      }))
                    }
                  />
                </label>
              )}
            </div>
            <div className="mc-rulePreview">
              <span>Preview</span>
              <strong>{ruleModal.rows.length} products</strong>
              <div>
                <b>Current</b>
                <em>Review required · No section · Existing/incorrect group</em>
                <span>→</span>
                <b>New</b>
                <em>
                  {ruleDraft.category || "Category"} ·{" "}
                  {ruleDraft.sectionMode === "new"
                    ? ruleDraft.sectionName
                    : formatSectionLabel(ruleDraft.sectionId)}{" "}
                  · {ruleDraft.groupName || "Count Group"}
                </em>
              </div>
            </div>
            <div className="mc-ruleModal__actions">
              <button
                type="button"
                className="mc-engineBtn mc-engineBtn--ghost"
                onClick={() => setRuleModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="mc-engineBtn mc-engineBtn--primary"
                disabled={
                  !clean(ruleDraft.category) ||
                  !(ruleDraft.sectionMode === "new"
                    ? clean(ruleDraft.sectionName)
                    : ruleDraft.sectionId) ||
                  !clean(ruleDraft.groupName)
                }
                onClick={saveRuleFromModal}
              >
                Save rule
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {ignoredRows.length ? (
        <section className="mc-enginePage__content mc-ignoredProductsCard" aria-label="Ignored products">
          <article className="mc-enginePanel">
            <div className="mc-enginePanel__header mc-reviewProducts__head">
              <div>
                <span className="mc-engineEyebrow">IGNORED PRODUCTS</span>
                <h3>{ignoredRows.length} product{ignoredRows.length === 1 ? "" : "s"} intentionally excluded</h3>
                <p>These products remain outside count sessions until you restore them or teach Morpheus how to classify them.</p>
              </div>
              <div className="mc-ignoredProductActions">
                <div className="mc-engineBadge">{ignoredRows.length} ignored</div>
                <button type="button" className="mc-engineBtn mc-engineBtn--ghost" onClick={restoreAllIgnoredRows}>Restore all to review</button>
              </div>
            </div>
            <div className="mc-ignoredProductsList">
              {ignoredRows.map((row) => {
                const suggestion = buildSuggestion(row, engine);
                return (
                  <div className="mc-ignoredProductRow" key={`ignored-${row.id || ignoredInventoryKey(row)}`}>
                    <div>
                      <strong>{row.product}</strong>
                      <small>{row.sku || "No SKU"} · {row.sourceCategory || "No source category"}</small>
                    </div>
                    <div className="mc-ignoredProductActions">
                      <button type="button" className="mc-engineBtn mc-engineBtn--ghost" onClick={() => restoreIgnoredRow(row)}>Restore to review</button>
                      <button type="button" className="mc-engineBtn mc-engineBtn--primary" onClick={() => openRuleModal([row], suggestion)}>Learn product</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </article>
        </section>
      ) : null}

      {showProducts ? (
        <section id="mc-product-review" className="mc-enginePage__content mc-reviewProducts">
          <article className="mc-enginePanel">
            <div className="mc-enginePanel__header mc-reviewProducts__head">
              <div>
                <h3>Product review</h3>
                <p>
                  Inspect the active snapshot without losing sight of
                  operational readiness.
                </p>
              </div>
              <div className="mc-engineBadge">
                {filteredRows.length} products
              </div>
            </div>

            <div className="mc-reviewToolbar">
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search product, SKU, category, or issue…"
              />
              <select
                value={activeFilter}
                onChange={(event) => setActiveFilter(event.target.value)}
              >
                <option value="all">All products</option>
                <option value="ready">Ready</option>
                <option value="category">Need category rule</option>
                <option value="section">Need section</option>
                <option value="group">Need Count Group</option>
                <option value="ignored">Ignored</option>
              </select>
              <button
                className="mc-engineBtn mc-engineBtn--ghost"
                type="button"
                onClick={() => {
                  setSearch("");
                  setActiveFilter("all");
                }}
              >
                Clear
              </button>
            </div>

            {filteredRows.length ? (
              <div className="mc-reviewTableWrap">
                <table className="mc-table mc-reviewTable">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Unit Intelligence</th>
                      <th>System SOH</th>
                      <th>Real SOH</th>
                      <th>Effective Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <strong>{row.product}</strong>
                          <small>
                            {row.sourceCategory || "No source category"}
                          </small>
                        </td>
                        <td>{row.sku || "—"}</td>
                        <td>
                          {row.category === "__unmapped__"
                            ? "Unmapped"
                            : row.category || "Unmapped"}
                        </td>
                        <td>
                          <span
                            className={`mc-readinessPill is-${row.readiness?.status || "category"}`}
                          >
                            {row.readiness?.reason || "Needs review"}
                          </span>
                        </td>
                        <td>
                          {row.isConverted ? (
                            <span className="mc-unitIntelligenceCell is-converted">
                              <strong>
                                {row.conversionLabel ||
                                  `${row.countUnit || "Count unit"} → ${row.baseUnit || "Base unit"}`}
                              </strong>
                              <small>
                                {row.autoDetected
                                  ? `Auto-detected · ${unitRuleScopeLabel(row)}`
                                  : unitRuleScopeLabel(row)}
                              </small>
                            </span>
                          ) : (
                            <span className="mc-unitIntelligenceCell is-standard">
                              <strong>Standard unit</strong>
                              <small>{unitRuleScopeLabel(row)} · 1:1</small>
                            </span>
                          )}
                        </td>
                        <td>{formatNumber(row.systemSoh)}</td>
                        <td>{formatNumber(row.realSoh ?? row.systemSoh)}</td>
                        <td>{formatMoney(row.effectiveCost ?? row.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mc-engineEmpty">
                <strong>No products match this view.</strong>
                <p>Change the filter or search term.</p>
              </div>
            )}
          </article>
        </section>
      ) : null}

      <section className="mc-countPreparation">
        <div className="mc-countPreparation__head">
          <div>
            <div className="mc-enginePage__eyebrow">Prepare count</div>
            <h3>Create operational work from trusted inventory.</h3>
            <p>
              Choose the count window, ownership scope, optional Count Groups,
              and—when needed—specific products. Morpheus shows exactly what will
              enter the session before anything is created.
            </p>
          </div>
          <div
            className={`mc-readinessPill ${isReady ? "is-ready" : "is-category"}`}
          >
            {isReady ? "Inventory ready" : "Resolve inventory first"}
          </div>
        </div>

        <div className="mc-countPreparation__grid">
          <label>
            <span>1. Cycle</span>
            <select
              value={selectedCycle}
              onChange={(event) => setSelectedCycle(event.target.value)}
              disabled={!isReady}
            >
              <option value="" disabled>
                Choose a cycle
              </option>
              {cycleOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>2. Section</span>
            <select
              value={selectedSection}
              onChange={(event) => setSelectedSection(event.target.value)}
              disabled={!isReady}
            >
              <option value="" disabled>
                Choose a section
              </option>
              {sectionOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <div className="mc-countPreparation__groups">
            <span>
              3. Count Groups <small>Optional</small>
            </span>
            {!selectedSectionOption ? (
              <div className="mc-countPreparation__disabled">
                Choose a section first
              </div>
            ) : applicableGroups.length <= 1 ? (
              <div className="mc-countPreparation__single">
                <strong>
                  {applicableGroups[0]?.name || "No Count Groups assigned"}
                </strong>
                <small>
                  {applicableGroups.length === 1
                    ? "Only group in this section — included automatically."
                    : "Configure Count Groups in Categories."}
                </small>
              </div>
            ) : (
              <details>
                <summary>
                  {selectedGroups.length
                    ? `${selectedGroups.length} selected`
                    : "All Count Groups"}
                </summary>
                <div className="mc-countPreparation__menu">
                  <button type="button" onClick={() => setSelectedGroups([])}>
                    All Count Groups
                  </button>
                  {applicableGroups.map((group) => (
                    <label key={group.name}>
                      <input
                        type="checkbox"
                        checked={selectedGroups.includes(group.name)}
                        onChange={() =>
                          setSelectedGroups((current) =>
                            current.includes(group.name)
                              ? current.filter((name) => name !== group.name)
                              : [...current, group.name],
                          )
                        }
                      />
                      <span>{group.name}</span>
                    </label>
                  ))}
                </div>
              </details>
            )}
          </div>
          <div className="mc-countPreparation__groups mc-countPreparation__products">
            <span>
              4. Count Product <small>Optional</small>
            </span>
            {!selectedSectionOption ? (
              <div className="mc-countPreparation__disabled">
                Choose a section first
              </div>
            ) : !productScopeRows.length ? (
              <div className="mc-countPreparation__disabled">
                No products in this scope
              </div>
            ) : (
              <McCountProductPicker
                rows={productScopeRows}
                selectedIds={selectedProductIds}
                onSelectionChange={setSelectedProductIds}
                search={productPickerSearch}
                onSearchChange={setProductPickerSearch}
              />
            )}
          </div>
        </div>

        <div className="mc-countPreparation__summary">
          <article>
            <span>Products included</span>
            <strong>{scopedRows.length}</strong>
          </article>
          <article>
            <span>Estimated count time</span>
            <strong>
              {scopedRows.length ? `${estimatedMinutes} min` : "—"}
            </strong>
          </article>
          <article>
            <span>Scope</span>
            <strong>{selectedSectionOption?.label || "Not selected"}</strong>
          </article>
          <article>
            <span>Count Groups</span>
            <strong>
              {selectedGroups.length
                ? selectedGroups.join(", ")
                : selectedSectionOption
                  ? "All applicable"
                  : "—"}
            </strong>
          </article>
          <article>
            <span>Count Product</span>
            <strong>
              {selectedProductIds.length
                ? `${selectedProductIds.length} selected`
                : selectedSectionOption
                  ? "All products"
                  : "—"}
            </strong>
          </article>
        </div>

        <div className="mc-countPreparation__footer">
          <p>
            {!isReady
              ? `${unresolvedCount} unresolved products remain blocked.`
              : !selectedCycle || !selectedSection
                ? "Choose a cycle and section to continue."
                : !scopedRows.length
                  ? "No ready products match this scope."
                  : `${scopedRows.length} trusted products will be prepared for this session.`}
          </p>
          <button
            className="mc-engineBtn mc-engineBtn--primary"
            type="button"
            disabled={!canPrepareScopedSession || sessionPreparing}
            onClick={() => {
              if (sessionPreparing) return;
              flushSync(() => setSessionPreparing(true));
              // Start only after React has committed the disabled/spinner state.
              requestAnimationFrame(() => {
                setTimeout(() => prepareNewSession(), 0);
              });
            }}
            aria-busy={sessionPreparing}
          >
            {sessionPreparing ? (
              <>
                <span className="mc-inlineSpinner" aria-hidden="true" />
                Preparing {scopedRows.length || ""} products...
              </>
            ) : (
              "Prepare Count Session"
            )}
          </button>
        </div>
      </section>

      {prepareSitePrompt ? (
        <div className="mc-reviewModal">
          <div
            className="mc-reviewModal__backdrop"
            onClick={() => setPrepareSitePrompt(false)}
          />
          <div className="mc-reviewModal__card">
            <div className="mc-enginePage__eyebrow">Select site</div>
            <h3>Where should this count run?</h3>
            <p>
              Inventory configuration remains globally scoped. Operational count
              sessions are always created for one site.
            </p>
            <label className="mc-prepareSitePrompt__field">
              <span>Site</span>
              <select
                value={prepareSiteScope}
                onChange={(event) => setPrepareSiteScope(event.target.value)}
              >
                <option value="" disabled>Choose a site</option>
                {prepareSiteOptions.map((scope) => (
                  <option key={scope} value={scope}>
                    {formatStoreLabel(scope)}
                  </option>
                ))}
              </select>
            </label>
            {!prepareSiteOptions.length ? (
              <p className="mc-prepareSitePrompt__warning">
                No active sites are available. Add or restore a site before preparing a count.
              </p>
            ) : null}
            <div className="mc-reviewModal__actions">
              <button
                className="mc-engineBtn mc-engineBtn--ghost"
                type="button"
                onClick={() => setPrepareSitePrompt(false)}
              >
                Cancel
              </button>
              <button
                className="mc-engineBtn mc-engineBtn--primary"
                type="button"
                disabled={!prepareSiteScope || sessionPreparing}
                aria-busy={sessionPreparing}
                onClick={() => {
                  if (sessionPreparing) return;
                  const target = prepareSiteScope;
                  flushSync(() => setSessionPreparing(true));
                  setPrepareSitePrompt(false);
                  requestAnimationFrame(() => setTimeout(() => prepareNewSession(target), 0));
                }}
              >
                {sessionPreparing ? (
                  <>
                    <span className="mc-inlineSpinner" aria-hidden="true" />
                    Preparing Live Count...
                  </>
                ) : (
                  "Continue"
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {overwritePrompt ? (
        <div className="mc-reviewModal">
          <div
            className="mc-reviewModal__backdrop"
            onClick={() => setOverwritePrompt(false)}
          />
          <div className="mc-reviewModal__card">
            <div className="mc-enginePage__eyebrow">
              Active session detected
            </div>
            <h3>This site already has an active count.</h3>
            <p>
              Resume the current active session, or replace only that active
              session. Completed and finalized sessions will not be overwritten.
            </p>
            <div className="mc-reviewModal__actions">
              <button
                className="mc-engineBtn mc-engineBtn--ghost"
                type="button"
                onClick={() => {
                  setOverwritePrompt(false);
                  setOverwriteMatches([]);
                  setPendingPrepareScope("");
                }}
              >
                Cancel
              </button>
              <button
                className="mc-engineBtn mc-engineBtn--ghost"
                type="button"
                onClick={resumeExistingLiveSession}
              >
                Resume active count
              </button>
              <button
                className="mc-engineBtn mc-engineBtn--primary"
                type="button"
                disabled={sessionPreparing}
                aria-busy={sessionPreparing}
                onClick={() => {
                  if (sessionPreparing) return;
                  flushSync(() => setSessionPreparing(true));
                  requestAnimationFrame(() => setTimeout(() => confirmOverwrite(), 0));
                }}
              >
                {sessionPreparing ? (
                  <>
                    <span className="mc-inlineSpinner" aria-hidden="true" />
                    Preparing Live Count...
                  </>
                ) : (
                  "Replace active count"
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      </>)}
      <McPageFooter />
    </div>
  );
}
